(function() {
  'use strict';

  // ─────────────────────────────────────────────────────────────────
  //  Boot — initKitConfigurator(root) is called per root by the
  //  dispatcher at the bottom of this file. The dispatcher runs on
  //  DOMContentLoaded and on shopify:section:load (Theme Customizer),
  //  matching the pattern used by pallet-configurator.js.
  //
  //  Kit-builder is a singleton on /pages/kit-builder by design — the
  //  localStorage key (bcs:kit-configurator:v1) and window.__KC debug
  //  hook are not section-scoped. If multi-instance support ever
  //  becomes a requirement, both would need to take the section id as
  //  a suffix.
  // ─────────────────────────────────────────────────────────────────
  function initKitConfigurator(root) {
  if (!root || root.dataset.kcInitialized === 'true') return;
  root.dataset.kcInitialized = 'true';
  const brandRegistryNode = root.querySelector('[data-kc-brand-registry]');
  const skuMapNode        = root.querySelector('[data-kc-sku-map]');
  const driversNode       = root.querySelector('[data-kc-drivers]');
  const customerTagsNode  = root.querySelector('[data-kc-customer-tags]');
  const configNode        = root.querySelector('[data-kc-config]');

  // Section settings — driver discovery uses the paginated collection JSON view.
  let KC_CONFIG = { driverCollectionHandle: 'kit-drivers' };
  try {
    if (configNode) Object.assign(KC_CONFIG, JSON.parse(configNode.textContent));
  } catch (e) { /* keep defaults */ }

  let BRAND_REGISTRY, SKU_MAP;
  try {
    BRAND_REGISTRY = JSON.parse(brandRegistryNode.textContent);
    SKU_MAP        = JSON.parse(skuMapNode.textContent);
  } catch (e) {
    console.error('[kit-configurator] data island parse failed', e);
    return;
  }

  // Third-party drivers — flat list of { brand, points, handle, sku, title,
  // variantId, price }. The `points` field is Single line text (numeric
  // strings like "100" or labels like "Unlimited"). Drop entries missing
  // brand, points, or variantId — those are admin-config errors. Coerce
  // points to a trimmed string so JS comparisons stay consistent.
  //
  // Driver data loads from /collections/{handle}?view=kit-drivers-json&page=N.
  // That keeps discovery token-free and lets Liquid render prices in the
  // current B2B customer context.
  let DRIVERS = [];
  let driversLoading = !!KC_CONFIG.driverCollectionHandle;
  let driversLoadError = false;
  let driverPriceRenderQueued = false;
  let kitPageViewTracked = false;
  const DRIVER_FETCH_MAX_PAGES = 10; // 10 * 250 = 2,500 collection products.

  function normalizeDriverBrand(value) {
    return String(value == null ? '' : value).trim();
  }
  function normalizeDriverBrandKey(value) {
    return normalizeDriverBrand(value).toLowerCase();
  }
  function normalizeDriverPoints(value) {
    return String(value == null ? '' : value).trim();
  }
  function queueDriverPriceRender() {
    if (driverPriceRenderQueued) return;
    driverPriceRenderQueued = true;
    requestAnimationFrame(() => {
      driverPriceRenderQueued = false;
      render();
    });
  }

  function applyDrivers(raw, options) {
    if (!Array.isArray(raw)) return;
    const pricesTrusted = !options || options.pricesTrusted !== false;
    DRIVERS = raw.filter(d => {
      if (!d) return false;
      const brandName = normalizeDriverBrand(d.brand);
      const pts = normalizeDriverPoints(d.points);
      if (brandName && pts && d.variantId) {
        d.brand = brandName;
        d.brandKey = normalizeDriverBrandKey(brandName);
        d.points = pts;
        d.price = pricesTrusted && typeof d.price === 'number' ? d.price : null;
        d.priceStatus = pricesTrusted ? 'loaded' : 'pending';
        if (!Array.isArray(d.companions)) d.companions = [];
        d.companions = d.companions.filter(c => c && c.variantId);
        d.companions.forEach(c => {
          c.price = pricesTrusted && typeof c.price === 'number' ? c.price : null;
          c.priceStatus = pricesTrusted ? 'loaded' : 'pending';
        });
        return true;
      }
      if (d.handle) {
        console.warn('[kit-configurator] dropping kit-driver "' + d.handle + '" — missing brand, points, or variantId');
      }
      return false;
    });
  }
  // Initial sync load from the Liquid data island. This is expected to be empty
  // in collection-driven mode; the async collection view populates DRIVERS.
  try {
    const raw = driversNode ? JSON.parse(driversNode.textContent) : [];
    applyDrivers(raw, { pricesTrusted: false });
  } catch (e) { /* default empty */ }

  async function fetchDriversViaCollectionView() {
    const handle = String(KC_CONFIG.driverCollectionHandle || 'kit-drivers').trim();
    if (!handle) return [];
    const drivers = [];
    let page = 1;
    let hasNextPage = true;
    while (hasNextPage && page <= DRIVER_FETCH_MAX_PAGES) {
      const url = '/collections/' + encodeURIComponent(handle) + '?view=kit-drivers-json&page=' + page;
      const resp = await fetch(url, { credentials: 'same-origin' });
      if (!resp.ok) throw new Error('Driver collection view ' + resp.status);
      const body = await resp.json();
      if (!body || !Array.isArray(body.drivers)) {
        throw new Error('Driver collection view: unexpected response shape');
      }
      drivers.push(...body.drivers);
      hasNextPage = body.hasNextPage === true;
      page += 1;
    }
    if (hasNextPage) {
      throw new Error('Driver collection exceeds ' + (DRIVER_FETCH_MAX_PAGES * 250) + ' products');
    }
    return drivers;
  }

  // Sort key for points values: numeric strings ascend by their integer; any
  // non-numeric value (e.g., "Unlimited") sorts last via +Infinity.
  function pointsSortValue(points) {
    const n = parseInt(String(points).trim(), 10);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
  }

  // Display label for a points value: "100 points" for numeric strings,
  // the raw text for non-numeric tier labels (e.g., "Unlimited").
  function formatPointsLabel(points) {
    const trimmed = String(points).trim();
    const n = parseInt(trimmed, 10);
    if (Number.isFinite(n) && String(n) === trimmed) {
      return n.toLocaleString() + ' points';
    }
    return trimmed;
  }

  // Customer tags from Liquid context. null/empty for guests. Some Liquid contexts
  // serialize tags as a comma-separated string — coerce defensively (matches the
  // pattern in valve-schedule.liquid and bcs-product-lookup.js).
  let CUSTOMER_TAGS = [];
  try {
    const raw = customerTagsNode ? JSON.parse(customerTagsNode.textContent) : [];
    if (typeof raw === 'string') {
      CUSTOMER_TAGS = raw.split(',').map(s => s.trim()).filter(Boolean);
    } else if (Array.isArray(raw)) {
      CUSTOMER_TAGS = raw;
    }
  } catch (e) { /* default empty */ }

  // True when the brand is gated and the current customer lacks the required tag.
  // Empty/missing requiredTag = not gated (returns false). Guests have no tags
  // so any gated brand is restricted.
  // Aligned with BCSProductLookup.isRestricted: literal "NULL" / "null" is
  // treated as un-gated (matches the convention used elsewhere on the storefront
  // for product-level restrictions).
  function isBrandRestricted(brand) {
    if (!brand) return false;
    const tag = brand.requiredTag ? String(brand.requiredTag).trim() : '';
    if (!tag || tag === 'NULL' || tag.toLowerCase() === 'null') return false;
    if (!CUSTOMER_TAGS.length) return true;
    const tagLower = tag.toLowerCase();
    return !CUSTOMER_TAGS.some(t => String(t).trim().toLowerCase() === tagLower);
  }

  // True when the brand is flagged Coming Soon — universally unavailable until
  // launched. Independent from per-customer access gating.
  function isBrandComingSoon(brand) {
    return !!(brand && brand.comingSoon);
  }
  function isBrandBlocked(brand) {
    return brand && brand.available === false || isBrandComingSoon(brand) || isBrandRestricted(brand);
  }

  // Sentinel value for the "No License" / hardware-only Core SKU option.
  // When state.coreTier === NO_LICENSE, the cart line is the bare
  // kit_hardware.product (e.g., HON-9000) instead of a kit_bundle product.
  // SMA Term and Device Upgrades steps are hidden in this mode. The leading
  // underscore ensures the sentinel can never collide with a Shopify
  // metaobject auto-handle (handles are slugged a-z/0-9/-).
  const NO_LICENSE = '__no-license';

  // Defensive: drop any null/non-object brand entries so a partially-populated
  // metaobject doesn't crash the page on render.
  Object.keys(BRAND_REGISTRY).forEach(k => {
    const b = BRAND_REGISTRY[k];
    if (!b || typeof b !== 'object' || !b.id || !Array.isArray(b.hardware)) {
      console.warn('[kit-configurator] dropping malformed brand entry', k, b);
      delete BRAND_REGISTRY[k];
    }
  });

  const sectionsHost = root.querySelector('[data-kc-sections]');
  const summaryHost  = root.querySelector('[data-kc-summary]');
  const CART_ADD_TIMEOUT_MS = 30000;
  let addingToCart = false;
  let eventsBound = false;
  let nextFocusHint = null;
  let interactionBlock = null;  // data-kc-block of the step the customer just clicked

  // BCSProductLookup is loaded with `defer` by the section, so it isn't
  // available when this inline script runs during page parse. Resolve it
  // lazily at the moment we actually need it (cart-add click time).
  function getBCS() { return window.BCSProductLookup; }

  function withTimeout(promise, ms, message) {
    let timer = null;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message || 'Request timed out')), ms);
    });
    return Promise.race([promise, timeout]).finally(() => {
      if (timer) clearTimeout(timer);
    });
  }

  function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ─────────────────────────────────────────────────────────────────
  //  Persistence
  // ─────────────────────────────────────────────────────────────────
  const STORAGE_KEY = 'bcs:kit-configurator:v1';
  const STALE_MS    = 30 * 24 * 60 * 60 * 1000;

  function saveState() {
    try {
      const blob = Object.assign({}, state, { savedAt: new Date().toISOString() });
      delete blob._smaResetNotice;
      delete blob._expandedSection;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
    } catch (e) {
      console.warn('[kit-configurator] save failed', e);
    }
  }

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed.savedAt || (Date.now() - new Date(parsed.savedAt).getTime() > STALE_MS)) return null;
      delete parsed.savedAt;
      return parsed;
    } catch (e) {
      console.warn('[kit-configurator] load failed', e);
      return null;
    }
  }

  function clearStorage() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  let saveTimer = null;
  function scheduleSave() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(saveState, 300);
  }

  // ─────────────────────────────────────────────────────────────────
  //  Toast
  // ─────────────────────────────────────────────────────────────────
  // Track the active toast + dismissal timer so rapid calls (e.g., several
  // Add to Cart clicks in succession) replace the existing toast instead of
  // stacking new nodes onto document.body.
  let _activeToast = null;
  let _activeToastTimer = null;
  function showToast(message, kind, action) {
    if (_activeToastTimer) { clearTimeout(_activeToastTimer); _activeToastTimer = null; }
    if (_activeToast) { _activeToast.remove(); _activeToast = null; }

    const t = document.createElement('div');
    t.className = 'kc-toast kc-toast--' + (kind || 'info');
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');

    const span = document.createElement('span');
    span.textContent = message;
    t.appendChild(span);

    if (action && action.label && action.href) {
      const link = document.createElement('a');
      link.href = action.href;
      link.textContent = action.label;
      link.className = 'kc-toast__action';
      t.appendChild(link);
    }

    document.body.appendChild(t);
    _activeToast = t;
    requestAnimationFrame(() => { t.classList.add('kc-toast--show'); });
    _activeToastTimer = setTimeout(() => {
      t.classList.remove('kc-toast--show');
      setTimeout(() => {
        t.remove();
        if (_activeToast === t) _activeToast = null;
      }, 300);
    }, 5000);
  }

  // ─────────────────────────────────────────────────────────────────
  //  State
  // ─────────────────────────────────────────────────────────────────
  // All non-quantity values are Shopify metaobject auto-handles (the slugged
  // form generated from the metaobject's name) — NOT the human label.
  const DEFAULT_STATE = {
    brand:        null,        // kit_brand handle, e.g. "honeywell"
    hardwareId:   null,        // kit_hardware handle, e.g. "hon-9000-controller"
    sdCard:       null,        // null = unanswered / no SD step, true = with SD card, false = without
    coreTier:     null,        // kit_bundle handle, e.g. "nc-9010"  (or NO_LICENSE)
    addons:       {},          // { "<kit_hardware_addon-handle>": qty }
    devicePacks:  [],          // kit_device_pack handles, e.g. ["device-25","device-50"]
    smaTerms:     [],          // term_id fields (NOT handles): ["1YR-INIT", "3YR", "5YR", "1YR"]
    accessories:  {},          // { "<kit_accessory-handle>": qty }
    driverNeeded:        null,  // null = unanswered, true = Yes, false = No
    driverBrand:         null,  // brand name string (matches DRIVERS[].brand)
    driverPoints:        null,  // points value as a string (e.g., "100", "Unlimited") matching DRIVERS[].points
    driverProductHandle: null,  // product handle of the specific driver SKU chosen at the confirmation step
    driverCompanions:    [],    // handles of opted-in complementary products (e.g., gateways the driver requires)
    quantity:     1,
    _expandedSection: null,    // transient UI state
    _smaResetNotice: false,    // transient: show "SMA reset" notice once
    _added: false,             // transient: set true after successful cart-add to prevent double-submit; cleared on any user-controllable setState
  };

  let state = { ...DEFAULT_STATE, devicePacks: [], smaTerms: [], accessories: {}, addons: {}, driverCompanions: [] };

  // Restore from localStorage if a fresh save exists
  const restored = loadState();
  if (restored) {
    Object.assign(state, restored);
    if (!Array.isArray(state.devicePacks)) state.devicePacks = [];
    if (Array.isArray(state.smaTerms)) {
      state.smaTerms = state.smaTerms.filter(Boolean);
    } else if (state.smaTerm) {
      state.smaTerms = [state.smaTerm];
    } else {
      state.smaTerms = [];
    }
    delete state.smaTerm;
    if (!state.accessories || typeof state.accessories !== 'object' || Array.isArray(state.accessories)) {
      state.accessories = {};
    }
    if (!state.addons || typeof state.addons !== 'object' || Array.isArray(state.addons)) {
      state.addons = {};
    }
  }

  // Schema-drift guard: drop fields that no longer resolve to known data.
  function validateRestoredState() {
    // Brand-level resets (invalid brand, blocked brand) clear driver state too.
    // Hardware-level resets do NOT clear drivers — drivers are independent of hardware.
    if (state.brand && !BRAND_REGISTRY[state.brand]) {
      state.brand = null;
      state.hardwareId = null;
      state.sdCard = null;
      state.coreTier = null;
      state.devicePacks = [];
      state.smaTerms = [];
      state.accessories = {};
      state.addons = {};
      state.driverNeeded = null;
      state.driverBrand = null;
      state.driverPoints = null;
      state.driverProductHandle = null;
      state.driverCompanions = [];
      return;
    }
    // Clear persisted state if the brand is now blocked (customer lost their
    // access tag since last visit, or the brand was flipped to coming-soon).
    if (state.brand && isBrandBlocked(BRAND_REGISTRY[state.brand])) {
      state.brand = null;
      state.hardwareId = null;
      state.sdCard = null;
      state.coreTier = null;
      state.devicePacks = [];
      state.smaTerms = [];
      state.accessories = {};
      state.addons = {};
      state.driverNeeded = null;
      state.driverBrand = null;
      state.driverPoints = null;
      state.driverProductHandle = null;
      state.driverCompanions = [];
      return;
    }
    if (state.brand && state.hardwareId) {
      const b = BRAND_REGISTRY[state.brand];
      const hw = b.hardware.find(h => h.id === state.hardwareId);
	      if (!hw) {
	        state.hardwareId = null;
	        state.sdCard = null;
	        state.coreTier = null;
        state.devicePacks = [];
        state.smaTerms = [];
        state.accessories = {};
	        state.addons = {};
	        return;
	      }
	      if (!hasSdStep(hw) || typeof state.sdCard !== 'boolean') {
	        // The customer declined the SD card but the no-SD products are gone;
	        // keeping the Core SKU would quietly switch it to the with-SD version.
	        if (state.sdCard === false && state.coreTier) {
	          state.coreTier = null;
	          state.smaTerms = [];
	        }
	        state.sdCard = null;
	      }
	      if (!hasCoreStep(hw)) {
	        state.coreTier = null;
	      } else if (state.coreTier === NO_LICENSE) {
	        // Sentinel survives only if the hardware metaobject still has a valid product reference
	        // (the no-SD one when "without SD card" is picked).
	        const map = skuFor();
	        if (!map || !map.hardware || !map.hardware.variantId) {
	          state.coreTier = null;
	        }
	      } else if (state.coreTier && (!visibleCoreTiers(hw).find(t => t.id === state.coreTier) || !coreTierAvailable(state.coreTier))) {
	        state.coreTier = null;
	        state.smaTerms = [];
	      }
      state.devicePacks = state.devicePacks.filter(pid => hw.devicePacks.find(p => p.id === pid));
      state.smaTerms = state.smaTerms.filter(termId => hw.smaTerms.find(t => t.id === termId));
	      // Verify the SMA SKU actually exists in SKU_MAP for this tier
	      const map = SKU_MAP[state.brand] && SKU_MAP[state.brand][state.hardwareId];
	      const smaTierId = selectedSmaBundleId();
	      state.smaTerms = state.smaTerms.filter(termId => (
	        smaTierId && map && map.sma && map.sma[smaTierId] && map.sma[smaTierId][termId]
	      ));
      // Drop accessory keys that no longer resolve under this hardware
      const validAccIds = new Set((hw.accessories || []).map(a => a.id));
      Object.keys(state.accessories).forEach(handle => {
        if (!validAccIds.has(handle)) delete state.accessories[handle];
      });
      // Drop addon keys that no longer resolve under this hardware
      const validAddonIds = new Set((hw.addons || []).map(a => a.id));
      Object.keys(state.addons).forEach(handle => {
        if (!validAddonIds.has(handle)) delete state.addons[handle];
      });
    } else {
      state.sdCard = null;
      state.accessories = {};
      state.addons = {};
    }
    // Drivers: coerce legacy values, drop deprecated field. Resolve-against-DRIVERS
    // validation is deferred to validateRestoredDriverState — it runs once on
    // boot AND again after the async driver collection fetch completes (since
    // DRIVERS starts empty in the async path).
    if (state.driverPoints != null) state.driverPoints = String(state.driverPoints);
    if ('driverConfirmed' in state) delete state.driverConfirmed;
    validateRestoredDriverState();
    state.quantity = clampQty(state.quantity);
  }

  function validateRestoredDriverState() {
    if (!DRIVERS.length) return; // nothing to validate against yet
    // Track whether a previously-picked driver gets dropped by this validation
    // pass so we can surface a toast + expand the Drivers section. Without this,
    // a customer who picked a driver yesterday but whose driver was un-tagged
    // overnight sees their selection silently vanish.
    const hadProductHandle = !!state.driverProductHandle;
    if (state.driverBrand) {
      const brandMatch = DRIVERS.find(d => d.brandKey === normalizeDriverBrandKey(state.driverBrand));
      if (!brandMatch) {
        state.driverBrand = null; state.driverPoints = null; state.driverProductHandle = null; state.driverCompanions = [];
      } else {
        state.driverBrand = brandMatch.brand;
      }
    }
    if (state.driverBrand && state.driverPoints) {
      state.driverPoints = normalizeDriverPoints(state.driverPoints);
      const hasCombo = DRIVERS.some(d => d.brandKey === normalizeDriverBrandKey(state.driverBrand) && d.points === state.driverPoints);
      if (!hasCombo) { state.driverPoints = null; state.driverProductHandle = null; state.driverCompanions = []; }
    }
    if (state.driverProductHandle) {
      const stillValid = state.driverNeeded === true && state.driverBrand && state.driverPoints &&
        DRIVERS.some(d => d.handle === state.driverProductHandle &&
                          d.brandKey === normalizeDriverBrandKey(state.driverBrand) && d.points === state.driverPoints);
      if (!stillValid) { state.driverProductHandle = null; state.driverCompanions = []; }
    }
    // Coerce + filter driverCompanions: keep only handles that exist in the
    // current driver's companion list. Drops stale picks if admin removed a
    // companion since last visit.
    if (!Array.isArray(state.driverCompanions)) state.driverCompanions = [];
    if (state.driverProductHandle && state.driverCompanions.length) {
      const d = DRIVERS.find(x => x.handle === state.driverProductHandle);
      const validHandles = new Set(((d && d.companions) || []).map(c => c.handle));
      state.driverCompanions = state.driverCompanions.filter(h => validHandles.has(h));
    } else if (!state.driverProductHandle) {
      state.driverCompanions = [];
    }
    if (hadProductHandle && !state.driverProductHandle) {
      state._expandedSection = 'drivers';
      if (typeof showToast === 'function') {
        showToast('Driver catalog updated — please re-select your driver.', 'info');
      }
    }
  }
  // ─────────────────────────────────────────────────────────────────
  //  Helpers (hoisted function declarations so they're available everywhere)
  // ─────────────────────────────────────────────────────────────────
  function clampQty(n) {
    const i = parseInt(n, 10);
    if (isNaN(i) || i < 1) return 1;
    if (i > 99) return 99;
    return i;
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function formatDriverPrice(item) {
    if (!item) return '';
    if (item.priceStatus === 'pending' || item.priceStatus === 'loading') return 'Loading price...';
    if (item.priceStatus === 'error') return 'Price unavailable';
    return formatMoney(item.price);
  }

  function isDriverPriceLoaded(item) {
    return !!item && item.priceStatus === 'loaded' && typeof item.price === 'number';
  }

  function hydrateB2BPrice(item) {
    if (!item || !item.handle || !item.variantId) return;
    // 'error' is terminal — without this guard, a failed item re-enters via the
    // render → hydrateVisibleDriverPrices loop and either burns CPU every frame
    // (no fetchBomMeta) or re-fires the failed fetch on every render.
    if (item.priceStatus === 'loaded' || item.priceStatus === 'loading' || item.priceStatus === 'error') return;
    const bcs = getBCS();
    if (!bcs || typeof bcs.fetchBomMeta !== 'function') {
      item.priceRetryCount = (item.priceRetryCount || 0) + 1;
      if (item.priceRetryCount > 40) {
        item.priceStatus = 'error';
        queueDriverPriceRender();
        return;
      }
      setTimeout(() => hydrateB2BPrice(item), 250);
      return;
    }
    item.priceRetryCount = 0;
    item.priceStatus = 'loading';
    bcs.fetchBomMeta(item.handle)
      .then(meta => {
        const variant = meta && Array.isArray(meta.variants)
          ? meta.variants.find(v => String(v.id) === String(item.variantId))
          : null;
        const dollars = variant && typeof variant.price === 'number' ? variant.price : null;
        if (dollars == null) {
          item.priceStatus = 'error';
          return;
        }
        item.price = Math.round(dollars * 100);
        item.priceStatus = 'loaded';
      })
      .catch(() => {
        item.priceStatus = 'error';
      })
      .finally(queueDriverPriceRender);
  }

  function makeCartSubmissionId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'kc:' + window.crypto.randomUUID();
    }
    return 'kc:' + Date.now().toString(36) + ':' + Math.random().toString(36).slice(2, 10);
  }

  function buildCartPayload(lines, submissionId) {
    const byVariant = new Map();
    lines.forEach(l => {
      const id = Number(l.variantId);
      const quantity = state.quantity * (l.qty || 1);
      if (!id || quantity < 1) return;
      const existing = byVariant.get(id);
      if (existing) {
        existing.quantity += quantity;
        return;
      }
      byVariant.set(id, {
        id: id,
        quantity: quantity,
        properties: submissionId
          ? { _kit_submission: submissionId, _kit_source: 'kit_builder' }
          : { _kit_source: 'kit_builder' }
      });
    });
    return Array.from(byVariant.values());
  }

  function cartMatchesSubmission(cart, payload, submissionId) {
    if (!cart || !Array.isArray(cart.items) || !submissionId || !Array.isArray(payload)) return false;
    const expected = new Map();
    payload.forEach(item => {
      expected.set(String(item.id), Number(item.quantity || 0));
    });
    const actual = new Map();
    cart.items.forEach(item => {
      const props = item && item.properties ? item.properties : null;
      if (!props || props._kit_submission !== submissionId) return;
      const variantId = String(item.variant_id || item.id || '');
      if (!variantId) return;
      actual.set(variantId, (actual.get(variantId) || 0) + Number(item.quantity || 0));
    });
    if (actual.size !== expected.size) return false;
    for (const [variantId, qty] of expected) {
      if (actual.get(variantId) !== qty) return false;
    }
    return true;
  }

  function safeAnalyticsPush(eventName, payload) {
    const eventPayload = { event: eventName };
    if (payload && typeof payload === 'object') Object.assign(eventPayload, payload);
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(eventPayload);
      }
    } catch (e) {}
  }

  function kitTrackingSnapshot() {
    const lines = buildCartLines();
    const b = brand();
    const hw = hardware();
    return {
      kit_source: 'kit_builder',
      kit_brand_id: state.brand || null,
      kit_brand_name: b ? b.name : null,
      kit_hardware_id: state.hardwareId || null,
      kit_hardware_name: hw ? hw.name : null,
      kit_sd_card: hasSdStep() ? state.sdCard : null,
      kit_core_tier: state.coreTier || null,
      kit_quantity: state.quantity,
      kit_driver_needed: state.driverNeeded === true,
      kit_driver_brand: state.driverBrand || null,
      kit_driver_points: state.driverPoints || null,
      kit_driver_product_handle: state.driverProductHandle || null,
      kit_driver_companion_count: Array.isArray(state.driverCompanions) ? state.driverCompanions.length : 0,
      kit_line_count: lines.length,
      kit_subtotal_cents: subtotalCents(),
      kit_ready: isReady(),
      kit_missing: whatsMissing()
    };
  }

  function emitKitEvent(kitEvent, detail) {
    const payload = Object.assign({
      kit_event: kitEvent,
      kit_page_path: window.location.pathname,
      kit_page_url: window.location.href
    }, kitTrackingSnapshot(), detail || {});
    safeAnalyticsPush('bcs_kit_builder', payload);
  }

  function trackKitPageViewOnce() {
    if (kitPageViewTracked) return;
    kitPageViewTracked = true;
    emitKitEvent('page_viewed', {
      kit_driver_mode: 'collection_view',
      kit_driver_collection_handle: KC_CONFIG.driverCollectionHandle || 'kit-drivers'
    });
  }

  function hydrateVisibleDriverPrices() {
    if (!DRIVERS.length) return;
    const handles = new Set();
    if (state.driverBrand && state.driverPoints) {
      DRIVERS
        .filter(d => d.brandKey === normalizeDriverBrandKey(state.driverBrand) && d.points === normalizeDriverPoints(state.driverPoints))
        .forEach(d => handles.add(d.handle));
    }
    if (state.driverProductHandle) {
      const selected = DRIVERS.find(d => d.handle === state.driverProductHandle);
      if (selected) {
        handles.add(selected.handle);
        (selected.companions || []).forEach(c => handles.add(c.handle));
      }
    }
    handles.forEach(handle => {
      const driver = DRIVERS.find(d => d.handle === handle);
      if (driver) {
        hydrateB2BPrice(driver);
        return;
      }
      DRIVERS.forEach(d => {
        const companion = (d.companions || []).find(c => c.handle === handle);
        if (companion) hydrateB2BPrice(companion);
      });
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/"/g, '\\"');
  }

  function captureFocusHint(el) {
    if (!el || !root.contains(el)) return null;
    const actionEl = el.closest && el.closest('[data-kc-action]');
    const blockEl = el.closest && el.closest('[data-kc-block]');
    if (!actionEl) return { block: blockEl ? blockEl.getAttribute('data-kc-block') : null };
    return {
      action: actionEl.getAttribute('data-kc-action'),
      value: actionEl.value || '',
      accId: actionEl.getAttribute('data-acc-id') || '',
      addonId: actionEl.getAttribute('data-addon-id') || '',
      section: actionEl.getAttribute('data-section') || '',
      block: blockEl ? blockEl.getAttribute('data-kc-block') : null
    };
  }

  function focusFirstInActiveSection() {
    const section = activeSection();
    const scope = section ? sectionsHost.querySelector(`[data-kc-block="${cssEscape(section)}"]`) : sectionsHost;
    const target = scope && scope.querySelector('input:not([disabled]), button:not([disabled]), a[href]');
    if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
  }

  function restoreFocusAfterRender() {
    const hint = nextFocusHint;
    nextFocusHint = null;
    if (!hint) return;
    let target = null;
    if (hint.action) {
      let selector = `[data-kc-action="${cssEscape(hint.action)}"]`;
      if (hint.value) selector += `[value="${cssEscape(hint.value)}"]`;
      if (hint.accId) selector += `[data-acc-id="${cssEscape(hint.accId)}"]`;
      if (hint.addonId) selector += `[data-addon-id="${cssEscape(hint.addonId)}"]`;
      if (hint.section) selector += `[data-section="${cssEscape(hint.section)}"]`;
      target = root.querySelector(selector);
    }
    if (target && !target.disabled && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
      return;
    }
    focusFirstInActiveSection();
  }

  function brand()    { return state.brand ? BRAND_REGISTRY[state.brand] : null; }
  function hardware() {
    const b = brand();
    if (!b || !state.hardwareId) return null;
    return b.hardware.find(h => h.id === state.hardwareId) || null;
  }
	  function rawSkuFor() {
	    if (!state.brand || !state.hardwareId) return null;
	    return SKU_MAP[state.brand] && SKU_MAP[state.brand][state.hardwareId] ? SKU_MAP[state.brand][state.hardwareId] : null;
	  }

	  // SKU map for the current picks. "Without SD card" swaps in the no-SD
	  // products filled in next to the regular ones, so cart lines, prices, and
	  // collapsed summaries follow the answer without knowing about SD cards.
	  function skuFor(sdCard = state.sdCard) {
	    const map = rawSkuFor();
	    if (!map || sdCard !== false || !hasSdStep()) return map;
	    return noSdMap(map, compositionMode());
	  }

	  const noSdMapCache = new WeakMap();
	  function noSdMap(map, mode) {
	    if (noSdMapCache.has(map)) return noSdMapCache.get(map);
	    const bundles = {};
	    Object.keys(map.bundles || {}).forEach(id => {
	      const b = map.bundles[id];
	      if (b && b.noSd && b.noSd.variantId) {
	        bundles[id] = Object.assign({}, b, b.noSd, { additionalProducts: b.additionalProducts });
	      } else if (mode !== COMPOSITION_MODES.BUNDLED_CORE) {
	        // The SD card ships on the hardware line here, not the bundle.
	        bundles[id] = b;
	      }
	      // bundled_core tier without a no-SD version: left out, so it's hidden.
	    });
	    const hardware = map.hardwareNoSd && map.hardwareNoSd.variantId ? map.hardwareNoSd : null;
	    const derived = Object.assign({}, map, { hardware, bundles });
	    noSdMapCache.set(map, derived);
	    return derived;
	  }

	  // The SD card question only appears for hardware with no-SD products filled in.
	  function hasSdStep(hw = hardware()) {
	    const map = hw && SKU_MAP[state.brand] ? SKU_MAP[state.brand][hw.id] : null;
	    if (!map) return false;
	    if (map.hardwareNoSd && map.hardwareNoSd.variantId) return true;
	    return compositionMode(hw) === COMPOSITION_MODES.BUNDLED_CORE && visibleCoreTiers(hw).some(t => {
	      const b = map.bundles && map.bundles[t.id];
	      return !!(b && b.noSd && b.noSd.variantId);
	    });
	  }

	  // A tier is available for an SD answer when its mode-specific core cart
	  // products resolve in that answer's SKU map, not merely when it has a key
	  // there (SKU_MAP keeps a key for every kit_bundle, even with no product).
	  function coreTierAvailable(tierId, sdCard = state.sdCard) {
	    const map = skuFor(sdCard);
	    if (tierId === NO_LICENSE) return !!(map && map.hardware && map.hardware.variantId);
	    return tierResolves(map, tierId);
	  }

	  function tierResolves(map, tierId) {
	    const b = map && map.bundles ? map.bundles[tierId] : null;
	    if (!b) return false;
	    if (compositionMode() === COMPOSITION_MODES.HARDWARE_PLUS_CORE) {
	      const addl = Array.isArray(b.additionalProducts) ? b.additionalProducts.filter(p => p && p.variantId) : [];
	      return !!(map.hardware && map.hardware.variantId && (addl.length > 0 || b.variantId));
	    }
	    return !!b.variantId;
	  }

	  function tierName(tier) {
	    if (state.sdCard === false && hasSdStep() && tier.displayNameNoSd) return tier.displayNameNoSd;
	    return tier.displayName || tier.label;
	  }

	  const COMPOSITION_MODES = {
	    BUNDLED_CORE: 'bundled_core',
	    HARDWARE_INCLUDES_CORE: 'hardware_includes_core',
	    HARDWARE_PLUS_CORE: 'hardware_plus_core'
	  };

	  function compositionMode(hw = hardware()) {
	    const mode = hw && hw.compositionMode ? String(hw.compositionMode).trim() : '';
	    return Object.values(COMPOSITION_MODES).includes(mode) ? mode : COMPOSITION_MODES.BUNDLED_CORE;
	  }

	  function hasCoreStep(hw = hardware()) {
	    return !!hw && compositionMode(hw) !== COMPOSITION_MODES.HARDWARE_INCLUDES_CORE;
	  }

	  function visibleCoreTiers(hw = hardware()) {
	    return ((hw && hw.coreTiers) || []).filter(t => t && t.displayInCoreStep !== false);
	  }

	  function defaultHardwareBundle(hw = hardware()) {
	    const defaults = ((hw && hw.coreTiers) || []).filter(t => t && t.isDefaultForHardware === true);
	    return defaults[0] || null;
	  }

	  function selectedSmaBundleId() {
	    const hw = hardware();
	    if (!hw) return null;
	    if (compositionMode(hw) === COMPOSITION_MODES.HARDWARE_INCLUDES_CORE) {
	      const defaultBundle = defaultHardwareBundle(hw);
	      return defaultBundle ? defaultBundle.id : null;
	    }
	    return state.coreTier && state.coreTier !== NO_LICENSE ? state.coreTier : null;
	  }

	  function hasSmaStep() {
	    const hw = hardware();
	    const map = skuFor();
	    const tierId = selectedSmaBundleId();
	    if (!hw || !map || !tierId || !map.sma || !map.sma[tierId]) return false;
	    return (hw.smaTerms || []).some(t => {
	      const meta = map.sma[tierId] && map.sma[tierId][t.id];
	      return !!(meta && meta.variantId);
	    });
	  }

	  function stepNumber(name) {
	    const i = getVisibleSteps().indexOf(name);
	    return i >= 0 ? i + 1 : '';
	  }

	  function stepPill(name) {
	    const n = stepNumber(name);
	    return n ? `<span class="kc-step-pill">${n}</span>` : '';
	  }

	  function getVisibleSteps() {
	    const steps = ['brand'];
	    if (state.brand) steps.push('hardware');
	    const hw = hardware();
	    if (hw && hasSdStep(hw)) steps.push('sd-card');
	    if (hw && hasCoreStep(hw)) steps.push('core-tier');
	    if (hw && Array.isArray(hw.devicePacks) && hw.devicePacks.length) steps.push('device-packs');
	    if (hw && hasSmaStep()) steps.push('sma');
	    if (hw && Array.isArray(hw.addons) && hw.addons.length) steps.push('addons');
	    if (hw && Array.isArray(hw.accessories) && hw.accessories.length) steps.push('accessories');
	    if (state.brand) steps.push('drivers');
	    return steps;
	  }

	  function coreLinePrice(mode, bundle, map) {
	    const addl = (bundle && Array.isArray(bundle.additionalProducts)) ? bundle.additionalProducts : [];
	    const additionalTotal = addl.reduce((sum, p) => sum + (p && p.variantId ? (p.price || 0) : 0), 0);
	    if (mode === COMPOSITION_MODES.HARDWARE_PLUS_CORE) {
	      const hardwareTotal = (map && map.hardware && map.hardware.variantId) ? (map.hardware.price || 0) : 0;
	      const coreTotal = additionalTotal || (bundle && bundle.variantId ? (bundle.price || 0) : 0);
	      return hardwareTotal + coreTotal;
	    }
	    return (bundle && bundle.variantId ? (bundle.price || 0) : 0) + additionalTotal;
	  }

	  function pushLine(lines, key, category, meta, qty = 1) {
	    if (!meta || !meta.variantId) return false;
	    lines.push({ key, category, sku: meta.sku, title: meta.title, variantId: meta.variantId, unitPrice: meta.price || 0, qty });
	    return true;
	  }

	  function validateCompositionConfig() {
	    Object.values(BRAND_REGISTRY).forEach(b => {
	      if (!b || !Array.isArray(b.hardware)) return;
	      b.hardware.forEach(hw => {
	        const mode = compositionMode(hw);
	        const tiers = Array.isArray(hw.coreTiers) ? hw.coreTiers : [];
	        const map = SKU_MAP[b.id] && SKU_MAP[b.id][hw.id] ? SKU_MAP[b.id][hw.id] : null;
	        if (mode === COMPOSITION_MODES.HARDWARE_INCLUDES_CORE) {
	          const defaults = tiers.filter(t => t && t.isDefaultForHardware === true);
	          if (defaults.length !== 1) {
	            console.warn('[kit-configurator] hardware "' + hw.id + '" uses hardware_includes_core and should have exactly one default kit_bundle for SMA resolution');
	          }
	        }
	        if (mode === COMPOSITION_MODES.HARDWARE_PLUS_CORE) {
	          visibleCoreTiers(hw).forEach(tier => {
	            const bundle = map && map.bundles ? map.bundles[tier.id] : null;
	            const addl = bundle && Array.isArray(bundle.additionalProducts) ? bundle.additionalProducts.filter(p => p && p.variantId) : [];
	            if (!map || !map.hardware || !map.hardware.variantId || (addl.length === 0 && !(bundle && bundle.variantId))) {
	              console.warn('[kit-configurator] hardware_plus_core tier "' + tier.id + '" on hardware "' + hw.id + '" is missing hardware or core product references');
	            }
	          });
	        }
	      });
	    });
	  }

	  validateCompositionConfig();
	  validateRestoredState();

	  function isSectionComplete(name) {
	    switch (name) {
	      case 'brand':     return state.brand !== null;
	      case 'hardware':  return state.hardwareId !== null;
	      case 'sd-card':   return !hasSdStep() || state.sdCard !== null;
	      case 'core-tier': return !hasCoreStep() || state.coreTier !== null;
	      case 'sma':       return !hasSmaStep() || state.smaTerms.length > 0;
      case 'drivers':
        // "No" branch is complete on its own; "Yes" branch needs all the way
        // through the explicit product-card pick.
        return state.driverNeeded === false ||
          (state.driverNeeded === true && !!state.driverBrand && !!state.driverPoints && !!state.driverProductHandle);
      default:          return true;
    }
  }

	  function activeSection() {
	    if (state._expandedSection) return state._expandedSection;
	    if (!isSectionComplete('brand'))     return 'brand';
	    if (!isSectionComplete('hardware'))  return 'hardware';
	    if (!isSectionComplete('sd-card'))   return 'sd-card';
	    if (hasCoreStep() && !isSectionComplete('core-tier')) return 'core-tier';
	    if (hasSmaStep() && !isSectionComplete('sma')) return 'sma';
	    return null;
	  }

	  function totalDevices() {
	    const hw = hardware();
	    if (!hw) return 0;
	    const tierId = selectedSmaBundleId();
	    const tier = tierId ? hw.coreTiers.find(t => t.id === tierId) : null;
	    const base = tier ? tier.devices : 0;
    const adds = state.devicePacks.reduce((sum, pid) => {
      const p = hw.devicePacks.find(x => x.id === pid);
      return sum + (p ? p.devices : 0);
    }, 0);
    return base + adds;
  }
	  function totalPoints() {
	    const hw = hardware();
	    if (!hw) return 0;
	    const tierId = selectedSmaBundleId();
	    const tier = tierId ? hw.coreTiers.find(t => t.id === tierId) : null;
    const base = tier ? tier.points : 0;
    const adds = state.devicePacks.reduce((sum, pid) => {
      const p = hw.devicePacks.find(x => x.id === pid);
      return sum + (p ? p.points : 0);
    }, 0);
    return base + adds;
  }

  // Stable categories used by the summary panel to bucket lines into
  // customer-facing groups (Hardware / Software / Accessories). Set on the
  // cart line itself rather than parsing line.key prefixes — prefix parsing
  // would silently break if a future key were ever added with a colliding name.
  const LINE_CATEGORY = {
    HARDWARE:    'hardware',
    SOFTWARE:    'software',
    ACCESSORIES: 'accessories',
  };

	  function buildCartLines() {
	    const lines = [];
	    const map = skuFor();
	    if (!map) return lines;
	    const hw = hardware();
	    const mode = compositionMode(hw);
	    // Defense in depth — every push is gated on a non-zero variantId. Render
	    // already filters misconfigured cards out, but if state ever points to an
	    // entry with an unfilled product reference we don't want a `{ id: 0 }` to
	    // reach Shopify's /cart/add.js (it would 422 the entire batch).
	    if (mode === COMPOSITION_MODES.HARDWARE_INCLUDES_CORE) {
	      pushLine(lines, 'hardware', LINE_CATEGORY.HARDWARE, map.hardware, 1);
	    } else if (state.coreTier === NO_LICENSE) {
	      // Hardware-only cart line — bare kit_hardware.product (e.g., HON-9000).
	      pushLine(lines, 'hardware', LINE_CATEGORY.HARDWARE, map.hardware, 1);
	    } else if (state.coreTier && map.bundles && map.bundles[state.coreTier]) {
	      const b = map.bundles[state.coreTier];
	      if (mode === COMPOSITION_MODES.HARDWARE_PLUS_CORE) {
	        pushLine(lines, 'hardware', LINE_CATEGORY.HARDWARE, map.hardware, 1);
	        const addl = Array.isArray(b.additionalProducts) ? b.additionalProducts : [];
	        addl.forEach((p, i) => pushLine(lines, 'bundle-additional:' + state.coreTier + ':' + i, LINE_CATEGORY.SOFTWARE, p, 1));
	        if (addl.length === 0 && b.variantId) {
	          console.warn('[kit-configurator] hardware_plus_core tier "' + state.coreTier + '" has no additional_products; falling back to bundle_product');
	          pushLine(lines, 'bundle', LINE_CATEGORY.SOFTWARE, b, 1);
	        }
	      } else {
	        pushLine(lines, 'bundle', LINE_CATEGORY.HARDWARE, b, 1);
	        const addl = Array.isArray(b.additionalProducts) ? b.additionalProducts : [];
	        addl.forEach((p, i) => pushLine(lines, 'bundle-additional:' + state.coreTier + ':' + i, LINE_CATEGORY.SOFTWARE, p, 1));
	      }
	    }
    Object.keys(state.addons || {}).forEach(handle => {
      const q = state.addons[handle];
      if (!q || q < 1) return;
      const a = (map.addons || {})[handle];
      if (!a || !a.variantId) return;
      lines.push({ key: 'addon:' + handle, category: LINE_CATEGORY.HARDWARE, sku: a.sku, title: a.title, variantId: a.variantId, unitPrice: a.price, qty: q });
    });
	    // Device packs and SMA only apply when the selected hardware path includes license coverage.
	    if (state.coreTier !== NO_LICENSE) {
	      state.devicePacks.forEach(code => {
	        const p = map.packs ? map.packs[code] : null;
	        if (!p || !p.variantId) return;
	        lines.push({ key: 'pack:' + code, category: LINE_CATEGORY.SOFTWARE, sku: p.sku, title: p.title, variantId: p.variantId, unitPrice: p.price, qty: 1 });
	      });
	      const smaTierId = selectedSmaBundleId();
	      if (smaTierId && map.sma && map.sma[smaTierId]) {
	        state.smaTerms.forEach(termId => {
	          const s = map.sma[smaTierId][termId];
	          if (!s || !s.variantId) return;
	          lines.push({ key: 'sma:' + termId, category: LINE_CATEGORY.SOFTWARE, sku: s.sku, title: s.title, variantId: s.variantId, unitPrice: s.price, qty: 1 });
	        });
	      }
    }
    Object.keys(state.accessories || {}).forEach(handle => {
      const q = state.accessories[handle];
      if (!q || q < 1) return;
      const a = (map.accessories || {})[handle];
      if (!a || !a.variantId) return;
      lines.push({ key: 'acc:' + handle, category: LINE_CATEGORY.ACCESSORIES, sku: a.sku, title: a.title, variantId: a.variantId, unitPrice: a.price, qty: q });
    });
    // Third-party driver (independent of hardware/tier). Customer's product
    // pick is identified by driverProductHandle (one of possibly several
    // products matching the chosen brand+points combo). Required complementary
    // products (e.g., a gateway the driver depends on) are opt-in via
    // driverCompanions; each checked handle adds its own cart line.
    if (state.driverNeeded === true && state.driverProductHandle) {
      const d = DRIVERS.find(x => x.handle === state.driverProductHandle);
      if (d && d.variantId) {
        lines.push({ key: 'driver', category: LINE_CATEGORY.SOFTWARE, sku: d.sku, title: d.title, variantId: d.variantId, unitPrice: isDriverPriceLoaded(d) ? d.price : 0, pricePending: !isDriverPriceLoaded(d), qty: 1 });
        (state.driverCompanions || []).forEach(handle => {
          const c = (d.companions || []).find(x => x.handle === handle);
          if (c && c.variantId) {
            lines.push({ key: 'driver-companion:' + handle, category: LINE_CATEGORY.SOFTWARE, sku: c.sku, title: c.title, variantId: c.variantId, unitPrice: isDriverPriceLoaded(c) ? c.price : 0, pricePending: !isDriverPriceLoaded(c), qty: 1 });
          }
        });
      }
    }
    return lines;
  }

  // Per-line subtotal (unit price × per-line qty). Used by both subtotalCents
  // and the summary panel — single source of truth for line math.
  function lineSubtotal(l) {
    if (l.pricePending) return 0;
    return l.unitPrice * (l.qty || 1);
  }

  function subtotalCents() {
    return buildCartLines().reduce((sum, l) => sum + lineSubtotal(l), 0) * state.quantity;
  }

  function missingDriverPrices() {
    if (state.driverNeeded !== true || !state.driverProductHandle) return false;
    const driver = DRIVERS.find(x => x.handle === state.driverProductHandle);
    if (!isDriverPriceLoaded(driver)) return true;
    return (state.driverCompanions || []).some(handle => {
      const companion = ((driver && driver.companions) || []).find(c => c.handle === handle);
      return !isDriverPriceLoaded(companion);
    });
  }

	  function whatsMissing() {
	    const missing = [];
	    if (!state.brand)      missing.push('Brand');
	    if (!state.hardwareId) missing.push('Hardware');
	    if (hasSdStep() && state.sdCard === null) missing.push('SD Card');
	    if (hasCoreStep() && !state.coreTier) missing.push('Core SKU');
	    // SMA Term is only required when a real bundle tier is picked. The
	    // "No License" sentinel ships hardware-only with no SMA coverage.
	    if (hasSmaStep() && state.smaTerms.length === 0) missing.push('SMA Terms');
	    if (missingDriverPrices()) missing.push('Driver price');
	    return missing;
	  }

  // Ready = required state keys are set AND the resulting cart payload actually
  // resolves to the expected lines. After the variantId guards landed, state
  // can be "complete" while buildCartLines silently drops a line (e.g., admin
  // un-linked a product reference). Validating payload presence here closes
  // the silent-partial-cart hole.
	  function isReady() {
	    if (whatsMissing().length) return false;
	    const lines = buildCartLines();
	    const mode = compositionMode();
	    if (mode === COMPOSITION_MODES.HARDWARE_INCLUDES_CORE) {
	      return lines.some(l => l.key === 'hardware') && (!hasSmaStep() || lines.some(l => l.key.indexOf('sma:') === 0));
	    }
	    if (state.coreTier === NO_LICENSE) {
	      return lines.some(l => l.key === 'hardware');
	    }
	    const hasCoreLine = mode === COMPOSITION_MODES.HARDWARE_PLUS_CORE
	      ? lines.some(l => l.key === 'hardware') && lines.some(l => l.key === 'bundle' || l.key.indexOf('bundle-additional:') === 0)
	      : lines.some(l => l.key === 'bundle');
	    return hasCoreLine && (!hasSmaStep() || lines.some(l => l.key.indexOf('sma:') === 0));
	  }

  // ─────────────────────────────────────────────────────────────────
  //  setState — single mutation point with cascade side-effects
  // ─────────────────────────────────────────────────────────────────
  function setState(patch) {
    nextFocusHint = captureFocusHint(document.activeElement);
    // Any user-controllable change clears the post-add lockout so the CTA re-enables
    // for a new kit configuration. Underscore-prefixed keys are transient UI flags
    // (_expandedSection, _smaResetNotice, _added itself) and don't count.
    if (state._added && Object.keys(patch).some(k => k[0] !== '_')) {
      state._added = false;
    }
    // Block selecting a restricted or coming-soon brand (defensive — input.disabled should already prevent this)
    if ('brand' in patch && patch.brand && patch.brand !== state.brand) {
      const target = BRAND_REGISTRY[patch.brand];
      if (target && isBrandBlocked(target)) {
        delete patch.brand;
      }
    }

    // Brand change cascades: clear hardware, tier, addons, packs, sma, accessories, driver
    if ('brand' in patch && patch.brand !== state.brand) {
      patch.hardwareId          = null;
      patch.sdCard              = null;
      patch.coreTier            = null;
      patch.devicePacks         = [];
      patch.smaTerms            = [];
      patch.accessories         = {};
      patch.addons              = {};
      patch.driverNeeded        = null;
      patch.driverBrand         = null;
      patch.driverPoints        = null;
      patch.driverProductHandle = null;
      patch.driverCompanions    = [];
    }

    // Hardware change cascades: clear tier, addons, packs, sma, accessories
    // (driver state is independent of hardware — preserved across hardware swaps)
    if ('hardwareId' in patch && patch.hardwareId !== state.hardwareId) {
      patch.sdCard      = null;
      patch.coreTier    = null;
      patch.devicePacks = [];
      patch.smaTerms    = [];
      patch.accessories = {};
      patch.addons      = {};
    }

    // Changing the SD card answer keeps the picked Core SKU — skuFor() swaps
    // it to the matching version. A tier with no version for the new answer
    // is cleared so the customer re-picks (cascades into the SMA reset below).
    if ('sdCard' in patch && patch.sdCard !== state.sdCard && state.coreTier && !('coreTier' in patch)) {
      if (!coreTierAvailable(state.coreTier, patch.sdCard)) patch.coreTier = null;
    }

    // Core tier change with prior SMA: reset SMA + show notice
    if ('coreTier' in patch && patch.coreTier !== state.coreTier && state.smaTerms.length > 0) {
      patch.smaTerms = [];
      patch._smaResetNotice = true;
    }
    // Picking "No License" hides the SMA + Device Upgrades steps; clear any
    // stale selections so they don't ride into the cart silently. (Reset
    // notice is cleared too — the SMA section won't be visible to display it.)
    if ('coreTier' in patch && patch.coreTier === NO_LICENSE) {
      patch.devicePacks = [];
      patch.smaTerms = [];
      patch._smaResetNotice = false;
    }
    // Clear the notice only when the customer actually re-picks a SMA term —
    // not on every unrelated mutation. (Previous behavior cleared on the next
    // setState for any reason, which made the notice flash for one cycle.)
    if ('smaTerms' in patch && patch.smaTerms.length > 0) {
      patch._smaResetNotice = false;
    }

    // Driver cascade: any change upstream of the product handle invalidates it
    // (and the companions tied to it), so the customer re-picks a product and
    // re-decides on companions.
    if ('driverNeeded' in patch && patch.driverNeeded !== true) {
      patch.driverBrand = null;
      patch.driverPoints = null;
      patch.driverProductHandle = null;
      patch.driverCompanions = [];
    }
    if ('driverBrand' in patch && patch.driverBrand !== state.driverBrand) {
      patch.driverPoints = null;
      patch.driverProductHandle = null;
      patch.driverCompanions = [];
    }
    if ('driverPoints' in patch && patch.driverPoints !== state.driverPoints) {
      patch.driverProductHandle = null;
      patch.driverCompanions = [];
    }
    if ('driverProductHandle' in patch && patch.driverProductHandle !== state.driverProductHandle) {
      patch.driverCompanions = [];
    }

    Object.assign(state, patch);

    // Auto-collapse: if the user just made a SELECTION that completes the active section,
    // clear the override so the next incomplete section becomes active.
    // Skip when the patch is itself the user opening a section via Edit (would be self-defeating).
    if (!('_expandedSection' in patch) && state._expandedSection && isSectionComplete(state._expandedSection)) {
      state._expandedSection = null;
    }

    emitKitEvent('state_changed', {
      changed_fields: Object.keys(patch)
    });
    scheduleSave();
    const anchor = captureCollapseAnchor();
    render();
    keepCollapsedStepInView(anchor);
  }

  // A pick that completes a step collapses it to a one-line summary. If the
  // customer was scrolled down inside that step (the driver product cards sit
  // below a tall brand grid), the page shrinks under them and the browser
  // leaves them at the bottom of the page — render() replaces every node, so
  // native scroll anchoring has nothing to hold on to. Anchor the step's
  // BOTTOM edge instead: the step shrinks upward and the next step stays
  // where it was on screen. Not the top edge — that scrolls up, which makes
  // the theme re-expand its sticky header over the step and leaves only the
  // footer below it.
  function captureCollapseAnchor() {
    const block = interactionBlock;
    interactionBlock = null;
    const el = block && sectionsHost.querySelector(`[data-kc-block="${cssEscape(block)}"]`);
    // Only an expanded step can collapse; picks inside a step that leave it
    // expanded (e.g. switching driver brand) keep the customer's position.
    if (!el || el.classList.contains('kc-section-block--collapsed')) return null;
    // Measured now: the collapse can briefly pull the summary CTA on screen,
    // hiding the fixed mobile bar until the scroll below brings it back.
    const bar = summaryHost.classList.contains('kc-summary-col--bar-shown') && summaryHost.querySelector('.kc-summary__bar');
    return { block, bottom: el.getBoundingClientRect().bottom, barHeight: bar ? bar.getBoundingClientRect().height : 0 };
  }

  function keepCollapsedStepInView(anchor) {
    if (!anchor) return;
    const el = sectionsHost.querySelector(`[data-kc-block="${cssEscape(anchor.block)}"]`);
    if (!el || !el.classList.contains('kc-section-block--collapsed')) return;
    const rect = el.getBoundingClientRect();
    // --header-end (main.js) is the collapsed header; the scroll below may be
    // upward, which re-expands it, so leave room for the taller header too.
    const headerEnd = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-end')) || 0;
    const header = document.querySelector('store-header');
    const menuHeight = header && header.classList.contains('is-out')
      ? parseFloat(getComputedStyle(header).getPropertyValue('--menu-height')) || 0
      : 0;
    const minTop = headerEnd + menuHeight + 16;
    // The fixed mobile summary bar covers the bottom of the screen.
    const maxBottom = window.innerHeight - 16 - anchor.barHeight;
    // Keep the bottom edge where it was. If it was below the visible area,
    // pinning it there would leave the next step below the fold, so put the
    // collapsed step at the top instead. Same if it's too tall.
    const targetTop = anchor.bottom > maxBottom
      ? minTop
      : Math.max(anchor.bottom - rect.height, minTop);
    window.scrollBy(0, rect.top - targetTop);
    updateSummaryBar();
  }


  // ─────────────────────────────────────────────────────────────────
  //  Render dispatcher
  // ─────────────────────────────────────────────────────────────────
 function render() {
    sectionsHost.innerHTML =
      renderBrand() +
      renderHardware() +
      renderSdCard() +
      renderCoreTier() +
      renderDevicePacks() +
      renderSma() +
      renderAddons() +
      renderAccessories() +
      renderDrivers() +
      renderQuantity();
    summaryHost.innerHTML = renderSummary();
    hydrateVisibleDriverPrices();
    bindEvents();
    // While a cart-add is in flight, disable every interactive control so a
    // mid-flight render (e.g., driver price hydration completing) cannot
    // re-enable picker inputs and let the customer mutate the in-flight payload.
    // renderSummary already handles the CTA label/disable; this covers the rest.
    if (addingToCart) {
      sectionsHost.querySelectorAll('input, button').forEach(el => el.setAttribute('disabled', 'disabled'));
      summaryHost.querySelectorAll('input, button').forEach(el => el.setAttribute('disabled', 'disabled'));
    }
    restoreFocusAfterRender();
    updateSummaryBar();
  }

  // ─────────────────────────────────────────────────────────────────
  //  Section renderers
  // ─────────────────────────────────────────────────────────────────
  function renderBrand() {
    const isComplete = isSectionComplete('brand');
    const isActive   = activeSection() === 'brand';
    const validBrands = Object.values(BRAND_REGISTRY).filter(b => b && b.id);

    if (validBrands.length === 0) {
      return `
        <div class="kc-section-block" data-kc-block="brand">
	          <h2 class="kc-block__title">${stepPill('brand')}Brand <span class="kc-required">Required</span></h2>
          <p class="kc-block__sub">No brand metaobjects found. Verify <code>kit_brand</code> entries exist and are storefront-accessible.</p>
        </div>`;
    }

    if (isComplete && !isActive) {
      const b = brand();
      if (!b) {
        // state.brand pointed to a brand that no longer exists — clear and re-render
        state.brand = null;
        state.hardwareId = null;
        return renderBrand();
      }
      const isOnlyOption = validBrands.length === 1;
      return `
        <div class="kc-section-block kc-section-block--collapsed" data-kc-block="brand">
          <div class="kc-collapsed">
	            ${stepPill('brand')}
            <div class="kc-collapsed__label">Brand:</div>
            <div class="kc-collapsed__value"><strong>${escapeHtml(b.name)}</strong></div>
            ${isOnlyOption
              ? '<span class="kc-auto-badge" title="Only brand available">Auto-selected</span>'
              : '<button type="button" class="kc-collapsed__edit" data-kc-action="edit-section" data-section="brand">Edit</button>'
            }
          </div>
        </div>`;
    }

    const cards = validBrands.map(b => {
      const checked = state.brand === b.id ? 'checked' : '';
      const hwCount = typeof b.hardwareCount === 'number' ? b.hardwareCount : (Array.isArray(b.hardware) ? b.hardware.length : 0);
      const comingSoon = isBrandComingSoon(b);
      const restricted = !comingSoon && isBrandRestricted(b); // coming-soon takes precedence visually
      const blocked = comingSoon || restricted;
      const logoBlock = b.logo
        ? `<div class="kc-brand-logo"><img src="${escapeHtml(b.logo)}" alt="${escapeHtml(b.name)} logo" loading="lazy" /></div>`
        : `<div class="kc-brand-logo" aria-hidden="true"></div>`;
      const banner = comingSoon
        ? '<div class="kc-card__coming-soon-banner">Coming Soon</div>'
        : (restricted ? '<div class="kc-card__restricted-banner">Restricted</div>' : '');
      const cardClasses = `kc-card kc-card--brand ${state.brand === b.id ? 'kc-card--selected' : ''} ${comingSoon ? 'kc-card--coming-soon' : ''} ${restricted ? 'kc-card--restricted' : ''}`;
      const inputAttrs = blocked ? 'disabled aria-disabled="true"' : '';
      return `
        <label class="${cardClasses}">
          <input type="radio" name="kc-brand" value="${escapeHtml(b.id)}" ${checked} ${inputAttrs} class="kc-card__input" data-kc-action="select-brand" />
          ${banner}
          ${logoBlock}
          <div class="kc-card__title">${escapeHtml(b.name)}</div>
          <div class="kc-card__sub">${hwCount} hardware ${hwCount === 1 ? 'platform' : 'platforms'}</div>
        </label>`;
    }).join('');

    const doneClass = isComplete ? 'kc-section-block--done' : '';
    return `
      <div class="kc-section-block ${doneClass}" data-kc-block="brand">
	        <h2 class="kc-block__title">${stepPill('brand')}Brand <span class="kc-required">Required</span></h2>
        <p class="kc-block__sub">Select the manufacturer for your controller kit.</p>
        <div class="kc-card-grid">${cards}</div>
      </div>`;
  }

  function renderHardware() {
    if (!state.brand) return ''; // hardware step doesn't exist until a brand is picked

    const isComplete = isSectionComplete('hardware');
    const isActive   = activeSection() === 'hardware';
    const b = brand();

    if (isComplete && !isActive) {
      const hw = hardware();
      if (!hw) {
        // hardwareId no longer resolves (e.g., metaobject deleted mid-session) — self-heal.
        state.hardwareId = null;
        state.sdCard = null;
        state.coreTier = null;
        state.devicePacks = [];
        state.smaTerms = [];
        state.accessories = {};
        state.addons = {};
        return renderHardware();
      }
      return `
        <div class="kc-section-block kc-section-block--collapsed" data-kc-block="hardware">
          <div class="kc-collapsed">
	            ${stepPill('hardware')}
            <div class="kc-collapsed__label">Hardware:</div>
            <div class="kc-collapsed__value"><strong>${escapeHtml(hw.name)}</strong></div>
            <button type="button" class="kc-collapsed__edit" data-kc-action="edit-section" data-section="hardware">Edit</button>
          </div>
        </div>`;
    }

    // Active picker — shown whenever the customer is on the Hardware step,
    // including when only one hardware platform exists. Cards use the same
    // image / title / inventory layout as the Hardware Add-on cards
    // (kc-card--accessory styling), but with a radio input, no short-desc,
    // no price, and no stepper.
    const cards = b.hardware.map(hw => {
      const checked = state.hardwareId === hw.id ? 'checked' : '';
      const sel     = state.hardwareId === hw.id ? 'kc-card--selected' : '';
      // Hardware product info comes from SKU_MAP[brand][hardwareId].hardware.
      // Optional — a hardware platform without a bare-hardware product
      // reference still works (just no image / inventory).
      const hwMap  = SKU_MAP[state.brand] && SKU_MAP[state.brand][hw.id];
      const hwMeta = hwMap && hwMap.hardware ? hwMap.hardware : null;
      const imageUrl = hwMeta && hwMeta.imageUrl ? hwMeta.imageUrl : '';
      const stockQty = hwMeta && typeof hwMeta.stockQty === 'number' ? hwMeta.stockQty : null;

      const imageBlock = imageUrl
        ? `<div class="kc-acc__image"><img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(hw.name)}" loading="lazy" /></div>`
        : `<div class="kc-acc__image" aria-hidden="true"></div>`;
      // Only render the stock line when we actually have product variant info;
      // a hardware platform without a linked product has nothing to "stock".
      const stockLine = (hwMeta && hwMeta.variantId)
        ? (stockQty > 0
            ? `<div class="kc-core__stock">${stockQty.toLocaleString()} in stock</div>`
            : `<div class="kc-core__stock kc-core__stock--available">Available to order</div>`)
        : '';

      return `
        <div class="kc-card kc-card--core kc-card--accessory ${sel}">
          <label class="kc-acc__toggle">
            <input type="radio" name="kc-hardware" value="${escapeHtml(hw.id)}" ${checked} class="kc-card__input" data-kc-action="select-hardware" />
            ${imageBlock}
            <div class="kc-core__sku">${escapeHtml(hw.name)}</div>
            ${stockLine}
          </label>
        </div>`;
    }).join('');

    const doneClass = isComplete ? 'kc-section-block--done' : '';
    return `
      <div class="kc-section-block ${doneClass}" data-kc-block="hardware">
	        <h2 class="kc-block__title">${stepPill('hardware')}Hardware <span class="kc-required">Required</span></h2>
        <p class="kc-block__sub">Select your controller platform.</p>
        <div class="kc-card-grid kc-card-grid--core">${cards}</div>
      </div>`;
  }

  function renderSdCard() {
    const hw = hardware();
    if (!hw || !hasSdStep(hw)) return '';

    const isComplete = isSectionComplete('sd-card');
    const isActive   = activeSection() === 'sd-card';

    if (isComplete && !isActive) {
      return `
        <div class="kc-section-block kc-section-block--collapsed" data-kc-block="sd-card">
          <div class="kc-collapsed">
            ${stepPill('sd-card')}
            <div class="kc-collapsed__label">SD Card:</div>
            <div class="kc-collapsed__value"><strong>${state.sdCard ? 'With SD card' : 'Without SD card'}</strong></div>
            <button type="button" class="kc-collapsed__edit" data-kc-action="edit-section" data-section="sd-card">Edit</button>
          </div>
        </div>`;
    }

    const optionCard = (val, label) => {
      const checked = state.sdCard === val ? 'checked' : '';
      const sel = state.sdCard === val ? 'kc-card--selected' : '';
      return `
        <label class="kc-card kc-card--core ${sel}">
          <input type="radio" name="kc-sd-card" value="${val ? 'yes' : 'no'}" ${checked} class="kc-card__input" data-kc-action="select-sd-card" />
          <div class="kc-core__sku">${escapeHtml(label)}</div>
        </label>`;
    };

    const doneClass = isComplete ? 'kc-section-block--done' : '';
    return `
      <div class="kc-section-block ${doneClass}" data-kc-block="sd-card">
        <h2 class="kc-block__title">${stepPill('sd-card')}SD Card <span class="kc-required">Required</span></h2>
        <p class="kc-block__sub">Do you need an SD card?</p>
        <div class="kc-driver-yesno">
          ${optionCard(false, 'No')}
          ${optionCard(true, 'Yes')}
        </div>
      </div>`;
  }

	  function renderCoreTier() {
	    const hw = hardware();
	    if (!hw) return '';
	    if (!hasCoreStep(hw)) return '';

	    const isComplete = isSectionComplete('core-tier');
	    const isActive   = activeSection() === 'core-tier';
	    const map = skuFor();
	    const mode = compositionMode(hw);

	    // Hardware-only mode shows the bare hardware product as the cart line.
	    // Available whenever kit_hardware.product resolves to a real variant.
	    const showNoLicense = mode === COMPOSITION_MODES.BUNDLED_CORE && !!(map && map.hardware && map.hardware.variantId);
	    const noLicenseLabel = (state.sdCard === false && hasSdStep(hw) && hw.noLicenseLabelNoSd) || hw.noLicenseLabel || 'No License';

    if (isComplete && !isActive) {
      // Collapsed state for No License selection.
      if (state.coreTier === NO_LICENSE) {
        if (!showNoLicense) {
          // Hardware product reference vanished — self-heal.
          state.coreTier = null;
          return renderCoreTier();
        }
        return `
          <div class="kc-section-block kc-section-block--collapsed" data-kc-block="core-tier">
            <div class="kc-collapsed">
	              ${stepPill('core-tier')}
              <div class="kc-collapsed__label">Core SKU:</div>
              <div class="kc-collapsed__value"><strong>${escapeHtml(noLicenseLabel)}</strong> · ${formatMoney(map.hardware.price)}</div>
              <button type="button" class="kc-collapsed__edit" data-kc-action="edit-section" data-section="core-tier">Edit</button>
            </div>
          </div>`;
      }

      const tier = hw.coreTiers.find(t => t.id === state.coreTier);
      const bundle = map && map.bundles ? map.bundles[state.coreTier] : null;
      if (!tier || !bundle) {
        // coreTier no longer resolves — self-heal and rerender as the active picker.
        state.coreTier = null;
        state.smaTerms = [];
        return renderCoreTier();
      }
      return `
        <div class="kc-section-block kc-section-block--collapsed" data-kc-block="core-tier">
          <div class="kc-collapsed">
	            ${stepPill('core-tier')}
            <div class="kc-collapsed__label">Core SKU:</div>
	            <div class="kc-collapsed__value"><strong>${escapeHtml(tierName(tier))}</strong> · ${tier.devices.toLocaleString()} devices · ${tier.points.toLocaleString()} points · ${formatMoney(coreLinePrice(mode, bundle, map))}</div>
            <button type="button" class="kc-collapsed__edit" data-kc-action="edit-section" data-section="core-tier">Edit</button>
          </div>
        </div>`;
    }

	    // Core SKU names and products depend on the SD card answer — wait for it.
	    if (hasSdStep(hw) && state.sdCard === null) {
	      return `
	        <div class="kc-section-block kc-section-block--disabled" data-kc-block="core-tier">
	          <h2 class="kc-block__title">${stepPill('core-tier')}Core SKU <span class="kc-required">Required</span></h2>
	          <p class="kc-block__sub">Answer the SD card question first to see matching Core SKUs.</p>
	        </div>`;
	    }

	    // Skip tiers whose mode-specific core cart products are unfilled.
	    // Otherwise the card would render at $0.00 and Add to Cart would fail.
	    const renderable = visibleCoreTiers(hw).filter(tier => {
	      if (tierResolves(map, tier.id)) return true;
	      if (map && map.bundles && !map.bundles[tier.id]) return false; // no no-SD version
	      if (mode === COMPOSITION_MODES.HARDWARE_PLUS_CORE) {
	        console.warn('[kit-configurator] skipping hardware_plus_core tier "' + tier.id + '" — hardware product or additional_products missing');
	      } else {
	        console.warn('[kit-configurator] skipping kit_bundle "' + tier.id + '" — bundle_product reference unfilled or unresolved');
	      }
	      return false;
	    });

    // Empty-state — no usable cards at all (no bundles AND no hardware-only fallback).
    if (renderable.length === 0 && !showNoLicense) {
      return `
        <div class="kc-section-block" data-kc-block="core-tier">
	          <h2 class="kc-block__title">${stepPill('core-tier')}Core SKU <span class="kc-required">Required</span></h2>
          <p class="kc-block__sub">This kit isn't fully configured yet. Please <a href="/pages/contact">contact us</a> for help.</p>
        </div>`;
    }

    const tierCards = renderable.map(tier => {
      const bundle = map && map.bundles ? map.bundles[tier.id] : null;
      const checked = state.coreTier === tier.id ? 'checked' : '';
      return `
        <label class="kc-card kc-card--core ${state.coreTier === tier.id ? 'kc-card--selected' : ''}">
          <input type="radio" name="kc-core-tier" value="${escapeHtml(tier.id)}" ${checked} class="kc-card__input" data-kc-action="select-core" />
          <div class="kc-core__sku">${escapeHtml(tierName(tier))}</div>
          <hr class="kc-core__divider" />
          <div class="kc-core__stats">
            <div class="kc-core__stat">
              <span class="kc-core__stat-label">Devices</span>
              <span class="kc-core__stat-value">${tier.devices.toLocaleString()}</span>
            </div>
            <div class="kc-core__stat">
              <span class="kc-core__stat-label">Points</span>
              <span class="kc-core__stat-value">${tier.points.toLocaleString()}</span>
            </div>
          </div>
          <hr class="kc-core__divider" />
	          <div class="kc-core__price">${formatMoney(coreLinePrice(mode, bundle, map))}</div>
        </label>`;
    }).join('');

    // Append the "No License" / hardware-only card last in the grid.
    const noLicenseCard = showNoLicense ? `
      <label class="kc-card kc-card--core ${state.coreTier === NO_LICENSE ? 'kc-card--selected' : ''}">
        <input type="radio" name="kc-core-tier" value="${escapeHtml(NO_LICENSE)}" ${state.coreTier === NO_LICENSE ? 'checked' : ''} class="kc-card__input" data-kc-action="select-core" />
        <div class="kc-core__sku">${escapeHtml(noLicenseLabel)}</div>
        <hr class="kc-core__divider" />
        <div class="kc-core__stats">
          <div class="kc-core__stat">
            <span class="kc-core__stat-label">Devices</span>
            <span class="kc-core__stat-value">0</span>
          </div>
          <div class="kc-core__stat">
            <span class="kc-core__stat-label">Points</span>
            <span class="kc-core__stat-value">0</span>
          </div>
        </div>
        <hr class="kc-core__divider" />
        <div class="kc-core__price">${formatMoney(map.hardware.price)}</div>
      </label>` : '';

    const cards = tierCards + noLicenseCard;

    const doneClass = isComplete ? 'kc-section-block--done' : '';
    return `
      <div class="kc-section-block ${doneClass}" data-kc-block="core-tier">
	        <h2 class="kc-block__title">${stepPill('core-tier')}Core SKU <span class="kc-required">Required</span></h2>
        <p class="kc-block__sub">Select the device/point capacity tier for your controller.</p>
        <div class="kc-card-grid kc-card-grid--core">${cards}</div>
      </div>`;
  }

  // Hardware Add-ons render — visually identical to Accessories (reuses
  // .kc-card--accessory and .kc-acc__* classes for layout parity). State
  // shape and stepper behavior also mirror accessories.
  function renderAddons() {
    const hw = hardware();
    if (!hw || !Array.isArray(hw.addons) || hw.addons.length === 0) return '';
    const map = skuFor();

    // Skip add-ons whose `product` reference is unfilled (variantId 0).
    const renderable = hw.addons.filter(ad => {
      const meta = map && map.addons ? map.addons[ad.id] : null;
      if (meta && meta.variantId) return true;
      console.warn('[kit-configurator] skipping kit_hardware_addon "' + ad.id + '" — product reference unfilled or unresolved');
      return false;
    });
    if (renderable.length === 0) return '';

    const cards = renderable.map(ad => {
      const meta = map.addons[ad.id];
      const price = meta.price;
      const title = meta.title ? meta.title : ad.label;
      const imageUrl = meta.imageUrl || '';
      const selectedQty = state.addons[ad.id];
      const isSelected = typeof selectedQty === 'number' && selectedQty >= 1;
      const recPill   = ad.recommended ? '<span class="kc-badge-recommended">Recommended</span>' : '';
      const catBadge  = ad.category ? `<span class="kc-acc__category">${escapeHtml(ad.category)}</span>` : '';
      const shortDescLine = meta.shortDesc ? `<div class="kc-acc__note">${escapeHtml(meta.shortDesc)}</div>` : '';
      const noteLine  = ad.note ? `<div class="kc-acc__note">${escapeHtml(ad.note)}</div>` : '';
      const stockQty  = typeof meta.stockQty === 'number' ? meta.stockQty : 0;
      const stockLine = stockQty > 0
        ? `<div class="kc-core__stock">${stockQty.toLocaleString()} in stock</div>`
        : `<div class="kc-core__stock kc-core__stock--available">Available to order</div>`;
      const imageBlock = imageUrl
        ? `<div class="kc-acc__image"><img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(title)}" loading="lazy" /></div>`
        : `<div class="kc-acc__image" aria-hidden="true"></div>`;
      const qtyAriaLabel = `Quantity for ${title}`;
      const stepperHtml = isSelected ? `
        <div class="kc-acc__stepper">
          <button type="button" class="kc-acc__stepper-btn" data-kc-action="addon-qty-dec" data-addon-id="${escapeHtml(ad.id)}" aria-label="Decrease ${escapeHtml(qtyAriaLabel)}">&minus;</button>
          <input type="number" inputmode="numeric" min="1" max="99" step="1" value="${selectedQty}" class="kc-acc__stepper-input" data-kc-action="addon-qty-input" data-addon-id="${escapeHtml(ad.id)}" aria-label="${escapeHtml(qtyAriaLabel)}" />
          <button type="button" class="kc-acc__stepper-btn" data-kc-action="addon-qty-inc" data-addon-id="${escapeHtml(ad.id)}" aria-label="Increase ${escapeHtml(qtyAriaLabel)}">+</button>
        </div>` : '';
      return `
        <div class="kc-card kc-card--core kc-card--accessory ${isSelected ? 'kc-card--selected' : ''}">
          <label class="kc-acc__toggle">
            <input type="checkbox" class="kc-card__input" value="${escapeHtml(ad.id)}" ${isSelected ? 'checked' : ''} data-kc-action="toggle-addon" />
            ${imageBlock}
            <div class="kc-core__sku">${escapeHtml(title)}</div>
            ${catBadge}
            ${shortDescLine}
            ${noteLine}
            ${stockLine}
            <hr class="kc-core__divider" />
            <div class="kc-acc__price-row">
              <div class="kc-core__price">${formatMoney(price)}</div>
              ${recPill}
            </div>
          </label>
          ${stepperHtml}
        </div>`;
    }).join('');

    const selectedCount = Object.keys(state.addons).filter(k => state.addons[k] >= 1).length;
    const doneClass = selectedCount > 0 ? 'kc-section-block--done' : '';
    return `
      <div class="kc-section-block ${doneClass}" data-kc-block="addons">
	        <h2 class="kc-block__title">${stepPill('addons')}Hardware Add-ons <span class="kc-optional">Optional</span></h2>
        <p class="kc-block__sub">Optional hardware add-ons that pair with your controller.</p>
        <div class="kc-card-grid kc-card-grid--core">${cards}</div>
      </div>`;
  }

  function renderDevicePacks() {
	    if (state.coreTier === NO_LICENSE) return ''; // packs are license-tier expansions
    const hw = hardware();
    if (!hw || !hw.devicePacks || hw.devicePacks.length === 0) return '';
    const map = skuFor();

    // Skip packs whose `product` reference is unfilled (variantId 0).
    const renderable = hw.devicePacks.filter(p => {
      const meta = map && map.packs ? map.packs[p.id] : null;
      if (meta && meta.variantId) return true;
      console.warn('[kit-configurator] skipping kit_device_pack "' + p.id + '" — product reference unfilled or unresolved');
      return false;
    });
    if (renderable.length === 0) return '';

    const cards = renderable.map(p => {
      const meta = map && map.packs ? map.packs[p.id] : null;
      const checked = state.devicePacks.includes(p.id) ? 'checked' : '';
      const title = meta.title ? meta.title : p.label;
      return `
        <label class="kc-card kc-card--core ${state.devicePacks.includes(p.id) ? 'kc-card--selected' : ''}">
          <input type="checkbox" value="${escapeHtml(p.id)}" ${checked} class="kc-card__input" data-kc-action="toggle-pack" />
          <div class="kc-core__sku">${escapeHtml(title)}</div>
          <hr class="kc-core__divider" />
          <div class="kc-core__stats">
            <div class="kc-core__stat">
              <span class="kc-core__stat-label">Devices</span>
              <span class="kc-core__stat-value">+${p.devices.toLocaleString()}</span>
            </div>
            <div class="kc-core__stat">
              <span class="kc-core__stat-label">Points</span>
              <span class="kc-core__stat-value">+${p.points.toLocaleString()}</span>
            </div>
          </div>
          <hr class="kc-core__divider" />
          <div class="kc-core__price">${formatMoney(meta.price)}</div>
        </label>`;
    }).join('');

	    const preview = selectedSmaBundleId()
	      ? `<p class="kc-capacity-preview">Selected capacity: <strong>${totalDevices().toLocaleString()}</strong> devices · <strong>${totalPoints().toLocaleString()}</strong> points</p>`
	      : '';

    const doneClass = state.devicePacks.length > 0 ? 'kc-section-block--done' : '';
    return `
      <div class="kc-section-block ${doneClass}" data-kc-block="device-packs">
	        <h2 class="kc-block__title">${stepPill('device-packs')}Device Upgrades <span class="kc-optional">Optional</span></h2>
	        <p class="kc-block__sub">Add capacity packs in any combination for this kit.</p>
        <div class="kc-card-grid kc-card-grid--core">${cards}</div>
        ${preview}
      </div>`;
  }

  function renderSma() {
	    if (state.coreTier === NO_LICENSE || !hasSmaStep()) return ''; // SMA is a license-coverage product
    const hw = hardware();
    if (!hw) return '';

    const map = skuFor();

	    const tier = selectedSmaBundleId();
	    if (!tier) {
	      return `
	        <div class="kc-section-block kc-section-block--disabled" data-kc-block="sma">
	          <h2 class="kc-block__title">${stepPill('sma')}SMA Terms <span class="kc-required">Required</span></h2>
	          <p class="kc-block__sub">Complete the previous required selections first to see matching SMA options.</p>
	        </div>`;
	    }

	    // Skip terms whose tier-resolved SMA product reference is unfilled.
    // SMA pricing is per-bundle (each kit_bundle has sma_init/sma_3yr/sma_5yr/sma_1yr
    // product fields), so a missing reference at THIS tier hides only that term
    // for this tier — other tiers may still have it.
    const renderable = hw.smaTerms.filter(t => {
      const meta = map.sma[tier] ? map.sma[tier][t.id] : null;
      if (meta && meta.variantId) return true;
      console.warn('[kit-configurator] skipping SMA term "' + t.id + '" for tier "' + tier + '" — product reference unfilled or unresolved');
      return false;
    });

    // Empty-state — no SMA terms resolve for this tier. Avoid leaving the
    // customer stuck on a blank required step.
	    if (renderable.length === 0) {
	      return `
	        <div class="kc-section-block" data-kc-block="sma">
	          <h2 class="kc-block__title">${stepPill('sma')}SMA Terms <span class="kc-required">Required</span></h2>
	          <p class="kc-block__sub">No SMA options are configured for this tier. Please <a href="/pages/contact">contact us</a> for help, or pick a different Core SKU.</p>
	        </div>`;
    }

    const cards = renderable.map(t => {
      const meta = map.sma[tier][t.id];
      const checked = state.smaTerms.includes(t.id) ? 'checked' : '';
      const badge = t.recommended ? '<span class="kc-badge-recommended">Recommended</span>' : '';
      const nudge = t.renewalNudge
        ? '<span class="kc-tooltip" tabindex="0" aria-label="For existing customers renewing coverage. New purchases should select 1-Year Initial.">&#9432;<span class="kc-tooltip__body">For existing customers renewing coverage. New purchases should select 1-Year Initial.</span></span>'
        : '';
      return `
        <label class="kc-card kc-card--sma ${state.smaTerms.includes(t.id) ? 'kc-card--selected' : ''}">
          <input type="checkbox" name="kc-sma-terms" value="${escapeHtml(t.id)}" ${checked} class="kc-card__input" data-kc-action="toggle-sma" />
          <div class="kc-card__title">${escapeHtml(t.label)}${nudge}</div>
          <div class="kc-card__sub">${escapeHtml(t.sub || '')}</div>
          <div class="kc-card__price-row">
            <div class="kc-card__price">${formatMoney(meta.price)}</div>
            ${badge}
          </div>
        </label>`;
    }).join('');

    const notice = state._smaResetNotice
      ? '<p class="kc-inline-notice">SMA reset because tier changed — please re-select.</p>'
      : '';

    const doneClass = isSectionComplete('sma') ? 'kc-section-block--done' : '';
    return `
      <div class="kc-section-block ${doneClass}" data-kc-block="sma">
	        <h2 class="kc-block__title">${stepPill('sma')}SMA Terms <span class="kc-required">Required</span></h2>
	        <p class="kc-block__sub">Software Maintenance Agreements for this kit.</p>
        ${notice}
        <div class="kc-card-grid kc-card-grid--core">${cards}</div>
      </div>`;
  }

  function renderAccessories() {
    const hw = hardware();
    if (!hw || !Array.isArray(hw.accessories) || hw.accessories.length === 0) return '';
    const map = skuFor();

    // Skip accessories whose `product` reference is unfilled in the metaobject
    // (variantId resolves to 0). Otherwise the card would render as a
    // selectable $0.00 tile that silently drops out of the cart.
    const renderable = hw.accessories.filter(acc => {
      const meta = map && map.accessories ? map.accessories[acc.id] : null;
      if (meta && meta.variantId) return true;
      console.warn('[kit-configurator] skipping kit_accessory "' + acc.id + '" — product reference unfilled or unresolved');
      return false;
    });
    if (renderable.length === 0) return '';

    const cards = renderable.map(acc => {
      const meta = map.accessories[acc.id];
      const price = meta.price;
      const title = meta.title ? meta.title : acc.label;
      const imageUrl = meta.imageUrl || '';
      const selectedQty = state.accessories[acc.id];
      const isSelected = typeof selectedQty === 'number' && selectedQty >= 1;
      const recPill   = acc.recommended ? '<span class="kc-badge-recommended">Recommended</span>' : '';
      const catBadge  = acc.category ? `<span class="kc-acc__category">${escapeHtml(acc.category)}</span>` : '';
      const shortDescLine = meta.shortDesc ? `<div class="kc-acc__note">${escapeHtml(meta.shortDesc)}</div>` : '';
      const noteLine  = acc.note ? `<div class="kc-acc__note">${escapeHtml(acc.note)}</div>` : '';
      const stockQty  = typeof meta.stockQty === 'number' ? meta.stockQty : 0;
      const stockLine = stockQty > 0
        ? `<div class="kc-core__stock">${stockQty.toLocaleString()} in stock</div>`
        : `<div class="kc-core__stock kc-core__stock--available">Available to order</div>`;
      const imageBlock = imageUrl
        ? `<div class="kc-acc__image"><img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(title)}" loading="lazy" /></div>`
        : `<div class="kc-acc__image" aria-hidden="true"></div>`;
      const qtyAriaLabel = `Quantity for ${title}`;
      const stepperHtml = isSelected ? `
        <div class="kc-acc__stepper">
          <button type="button" class="kc-acc__stepper-btn" data-kc-action="acc-qty-dec" data-acc-id="${escapeHtml(acc.id)}" aria-label="Decrease ${escapeHtml(qtyAriaLabel)}">&minus;</button>
          <input type="number" inputmode="numeric" min="1" max="99" step="1" value="${selectedQty}" class="kc-acc__stepper-input" data-kc-action="acc-qty-input" data-acc-id="${escapeHtml(acc.id)}" aria-label="${escapeHtml(qtyAriaLabel)}" />
          <button type="button" class="kc-acc__stepper-btn" data-kc-action="acc-qty-inc" data-acc-id="${escapeHtml(acc.id)}" aria-label="Increase ${escapeHtml(qtyAriaLabel)}">+</button>
        </div>` : '';
      return `
        <div class="kc-card kc-card--core kc-card--accessory ${isSelected ? 'kc-card--selected' : ''}">
          <label class="kc-acc__toggle">
            <input type="checkbox" class="kc-card__input" value="${escapeHtml(acc.id)}" ${isSelected ? 'checked' : ''} data-kc-action="toggle-accessory" />
            ${imageBlock}
            <div class="kc-core__sku">${escapeHtml(title)}</div>
            ${catBadge}
            ${shortDescLine}
            ${noteLine}
            ${stockLine}
            <hr class="kc-core__divider" />
            <div class="kc-acc__price-row">
              <div class="kc-core__price">${formatMoney(price)}</div>
              ${recPill}
            </div>
          </label>
          ${stepperHtml}
        </div>`;
    }).join('');

    const selectedCount = Object.keys(state.accessories).filter(k => state.accessories[k] >= 1).length;
    const doneClass = selectedCount > 0 ? 'kc-section-block--done' : '';
    return `
      <div class="kc-section-block ${doneClass}" data-kc-block="accessories">
	        <h2 class="kc-block__title">${stepPill('accessories')}Accessories <span class="kc-optional">Optional</span></h2>
        <p class="kc-block__sub">Add commonly paired accessories to complete your kit.</p>
        <div class="kc-card-grid kc-card-grid--core">${cards}</div>
      </div>`;
  }

  // Third-Party Drivers — Step 8, Optional. Independent of hardware (drivers
  // are a global product collection). Four nested questions: Yes/No, brand,
  // points, then an explicit product confirmation card before the cart line
  // is generated. Auto-collapses once the section is complete.
  function renderDrivers() {
    if (!state.brand) return '';
    if (driversLoadError) {
      return `
        <div class="kc-section-block" data-kc-block="drivers">
	          <h2 class="kc-block__title">${stepPill('drivers')}Third-Party Drivers <span class="kc-optional">Optional</span></h2>
          <p class="kc-block__sub">Driver options could not be loaded. You can still add the kit without a driver, or clear the restored driver selection and try again.</p>
          <button type="button" class="kc-collapsed__edit" data-kc-action="clear-driver-selection">Clear driver selection</button>
        </div>`;
    }
    if (driversLoading && DRIVERS.length === 0) {
      return `
        <div class="kc-section-block" data-kc-block="drivers" aria-busy="true">
	          <h2 class="kc-block__title">${stepPill('drivers')}Third-Party Drivers <span class="kc-optional">Optional</span></h2>
          <p class="kc-block__sub">Loading driver options and B2B pricing...</p>
        </div>`;
    }
    if (DRIVERS.length === 0) return '';

    const isComplete = isSectionComplete('drivers');
    const isActive   = state._expandedSection === 'drivers';

    // Required Components sub-block — rendered INSIDE the main Drivers section
    // (not as a separate kc-section-block) so it can't be overlooked. Visible
    // in BOTH the collapsed and expanded states whenever the picked driver has
    // complementary products defined. No section-block wrapper here.
    let companionsInner = '';
    if (state.driverProductHandle) {
      const dForComp = DRIVERS.find(x => x.handle === state.driverProductHandle);
      if (dForComp && Array.isArray(dForComp.companions) && dForComp.companions.length > 0) {
        const companionCards = dForComp.companions.map(c => {
          const checked = state.driverCompanions.includes(c.handle) ? 'checked' : '';
          const sel     = state.driverCompanions.includes(c.handle) ? 'kc-card--selected' : '';
          const vendorLine    = c.vendor    ? `<div class="kc-core__vendor">${escapeHtml(c.vendor)}</div>` : '';
          const shortDescLine = c.shortDesc ? `<div class="kc-acc__note">${escapeHtml(c.shortDesc)}</div>` : '';
          return `
            <label class="kc-card kc-card--core kc-card--driver ${sel}">
              <input type="checkbox" class="kc-card__input" value="${escapeHtml(c.handle)}" ${checked} data-kc-action="toggle-driver-companion" />
              <div class="kc-core__sku">${escapeHtml(c.title)}</div>
              ${vendorLine}
              ${shortDescLine}
              <hr class="kc-core__divider" />
              <div class="kc-core__price">${escapeHtml(formatDriverPrice(c))}</div>
            </label>`;
        }).join('');
        companionsInner = `
          <h3 class="kc-subsection-title">Required Components</h3>
          <p class="kc-driver-companions-hint">This driver requires the following to function. Add any you need — skip if you already have one.</p>
          <div class="kc-card-grid kc-card-grid--core">${companionCards}</div>`;
      }
    }

    // Collapsed summary view — shown once the section is "complete" and the
    // user isn't actively editing it. Companions block (if any) appears below.
    // The summary line folds in any picked companions so the closed view
    // visibly reflects the customer's full selection (not just the driver).
    if (isComplete && !isActive) {
      let summaryHtml;
      if (state.driverNeeded === false) {
	        summaryHtml = '<strong>None</strong>';
      } else {
        const d = DRIVERS.find(x => x.handle === state.driverProductHandle);
        if (!d) {
          // Self-heal: resolved driver vanished — drop the pick and re-render.
          state.driverProductHandle = null;
          return renderDrivers();
        }
        const parts = [`<strong>${escapeHtml(d.title)}</strong> · ${escapeHtml(formatDriverPrice(d))}`];
        (state.driverCompanions || []).forEach(handle => {
          const c = (d.companions || []).find(x => x.handle === handle);
          if (c) parts.push(`<strong>${escapeHtml(c.title)}</strong> · ${escapeHtml(formatDriverPrice(c))}`);
        });
        summaryHtml = parts.join(' + ');
      }
      return `
        <div class="kc-section-block kc-section-block--collapsed" data-kc-block="drivers">
          <div class="kc-collapsed">
            ${stepPill('drivers')}
            <div class="kc-collapsed__label">Driver:</div>
            <div class="kc-collapsed__value">${summaryHtml}</div>
            <button type="button" class="kc-collapsed__edit" data-kc-action="edit-section" data-section="drivers">Edit</button>
          </div>
          ${companionsInner}
        </div>`;
    }

    const yesNoCard = (val, label) => {
      const checked = state.driverNeeded === val ? 'checked' : '';
      const sel = state.driverNeeded === val ? 'kc-card--selected' : '';
      return `
        <label class="kc-card kc-card--core ${sel}">
          <input type="radio" name="kc-driver-needed" value="${val ? 'yes' : 'no'}" ${checked} class="kc-card__input" data-kc-action="select-driver-needed" />
          <div class="kc-core__sku">${escapeHtml(label)}</div>
        </label>`;
    };
    const yesNoHtml = `
      <div class="kc-driver-yesno">
        ${yesNoCard(true, 'Yes')}
        ${yesNoCard(false, 'No')}
      </div>`;

    let brandPickerHtml = '';
    let pointsPickerHtml = '';
    let productPickerHtml = '';
    if (state.driverNeeded === true) {
      // Distinct brand list, alphabetized for stable display.
      const brandSet = new Set();
      DRIVERS.forEach(d => { if (d.brand) brandSet.add(d.brand); });
      const brands = Array.from(brandSet).sort((a, b) => a.localeCompare(b));

      const brandCards = brands.map(brand => {
        const checked = state.driverBrand === brand ? 'checked' : '';
        const sel = state.driverBrand === brand ? 'kc-card--selected' : '';
        return `
          <label class="kc-card kc-card--core ${sel}">
            <input type="radio" name="kc-driver-brand" value="${escapeHtml(brand)}" ${checked} class="kc-card__input" data-kc-action="select-driver-brand" />
            <div class="kc-core__sku">${escapeHtml(brand)}</div>
          </label>`;
      }).join('');
      brandPickerHtml = `
        <h3 class="kc-subsection-title">What brand are you integrating?</h3>
        <div class="kc-card-grid kc-card-grid--core">${brandCards}</div>`;

      if (state.driverBrand) {
        // Distinct points values for the picked brand. Numeric values ascending,
        // non-numeric tier labels (e.g., "Unlimited") pinned last. Multiple
        // products sharing the same (brand, points) collapse into one card here
        // — the customer disambiguates at the product-confirmation step below.
        const distinctPoints = Array.from(new Set(
          DRIVERS.filter(d => d.brandKey === normalizeDriverBrandKey(state.driverBrand)).map(d => d.points)
        )).sort((a, b) => pointsSortValue(a) - pointsSortValue(b));

        const pointsCards = distinctPoints.map(pts => {
          const checked = state.driverPoints === pts ? 'checked' : '';
          const sel = state.driverPoints === pts ? 'kc-card--selected' : '';
          return `
            <label class="kc-card kc-card--core ${sel}">
              <input type="radio" name="kc-driver-points" value="${escapeHtml(pts)}" ${checked} class="kc-card__input" data-kc-action="select-driver-points" />
              <div class="kc-core__sku">${escapeHtml(formatPointsLabel(pts))}</div>
            </label>`;
        }).join('');
        pointsPickerHtml = `
          <h3 class="kc-subsection-title">How many points do you need?</h3>
          <div class="kc-card-grid kc-card-grid--core">${pointsCards}</div>`;

        // Product pick — once a points tier is picked, show every product
        // matching the (brand, points) combo. Usually one, but sometimes
        // multiple SKUs share the same brand+points. The customer's pick is
        // the specific product handle.
        if (state.driverPoints) {
          const matches = DRIVERS.filter(x => x.brandKey === normalizeDriverBrandKey(state.driverBrand) && x.points === normalizeDriverPoints(state.driverPoints));
          if (matches.length > 0) {
            const productCards = matches.map(d => {
              const checked = state.driverProductHandle === d.handle ? 'checked' : '';
              const sel = state.driverProductHandle === d.handle ? 'kc-card--selected' : '';
              const vendorLine    = d.vendor    ? `<div class="kc-core__vendor">${escapeHtml(d.vendor)}</div>` : '';
              const shortDescLine = d.shortDesc ? `<div class="kc-acc__note">${escapeHtml(d.shortDesc)}</div>` : '';
              return `
                <label class="kc-card kc-card--core kc-card--driver ${sel}">
                  <input type="radio" name="kc-driver-product" value="${escapeHtml(d.handle)}" ${checked} class="kc-card__input" data-kc-action="select-driver-product" />
                  <div class="kc-core__sku">${escapeHtml(d.title)}</div>
                  ${vendorLine}
                  ${shortDescLine}
                  <hr class="kc-core__divider" />
                  <div class="kc-core__price">${escapeHtml(formatDriverPrice(d))}</div>
                </label>`;
            }).join('');
            productPickerHtml = `
              <h3 class="kc-subsection-title">Select your driver</h3>
              <div class="kc-card-grid kc-card-grid--core">${productCards}</div>`;
          }
        }
      }
    }

    const doneClass = isComplete ? 'kc-section-block--done' : '';
    return `
      <div class="kc-section-block ${doneClass}" data-kc-block="drivers">
	        <h2 class="kc-block__title">${stepPill('drivers')}Third-Party Drivers <span class="kc-optional">Optional</span></h2>
        <p class="kc-block__sub">Add a driver if you're integrating with a third-party system.</p>
        <h3 class="kc-subsection-title">Do you need a third-party driver?</h3>
        ${yesNoHtml}
        ${brandPickerHtml}
        ${pointsPickerHtml}
        ${productPickerHtml}
        ${companionsInner}
      </div>`;
  }

  function renderQuantity() {
    if (!state.brand) return '';
    const doneClass = state.quantity > 1 ? 'kc-section-block--done' : '';
    return `
      <div class="kc-section-block ${doneClass}" data-kc-block="quantity">
        <h2 class="kc-block__title">Kit Quantity</h2>
        <p class="kc-block__sub">How many identical kits should we add to your cart?</p>
        <div class="kc-stepper">
          <button type="button" class="kc-stepper__btn" data-kc-action="qty-dec" aria-label="Decrease quantity">&minus;</button>
          <input type="number" inputmode="numeric" min="1" max="99" step="1" value="${state.quantity}" class="kc-stepper__input" data-kc-action="qty-input" aria-label="Kit quantity" />
          <button type="button" class="kc-stepper__btn" data-kc-action="qty-inc" aria-label="Increase quantity">+</button>
        </div>
      </div>`;
  }

  function renderSummary() {
    const lines = buildCartLines();

    // Group cart lines into customer-facing buckets via line.category (set in
    // buildCartLines). Within each bucket, lines preserve buildCartLines' push
    // order: Hardware = bundle → addons; Software = packs → SMA;
    // Accessories = accessories. Reorder there if you want different display order.
    // Empty groups are hidden — the summary stays compact when nothing's selected
    // in a category yet.
    const groups = [
      { title: 'Hardware',    lines: lines.filter(l => l.category === LINE_CATEGORY.HARDWARE) },
      { title: 'Software',    lines: lines.filter(l => l.category === LINE_CATEGORY.SOFTWARE) },
      { title: 'Accessories', lines: lines.filter(l => l.category === LINE_CATEGORY.ACCESSORIES) },
    ];

    const renderLine = l => {
      const lineQty = state.quantity * (l.qty || 1);
      const linePrice = l.pricePending ? 'Loading price...' : formatMoney(lineSubtotal(l) * state.quantity);
      return `
        <div class="kc-summary__line">
          <div class="kc-summary__line-title">${escapeHtml(l.title)}</div>
          <div class="kc-summary__line-qty">Qty ${lineQty}</div>
          <div class="kc-summary__line-price">${escapeHtml(linePrice)}</div>
        </div>`;
    };

    const linesHtml = lines.length === 0
      ? '<p class="kc-summary__empty">No items selected yet.</p>'
      : groups.filter(g => g.lines.length > 0).map(g => `
          <div class="kc-summary__group">
            <h3 class="kc-summary__group-title">${escapeHtml(g.title)}</h3>
            ${g.lines.map(renderLine).join('')}
          </div>`).join('');

    const missing = whatsMissing();
    // CTA precedence: addingToCart > state._added (post-success lockout) > !isReady() > enabled.
    // addingToCart shows "Adding..." with everything disabled (also enforced in render()).
    // state._added shows "Kit Added ✓" and prevents double-submit until the customer
    // makes another selection (which clears _added via setState).
    const ctaDisabled = (!isReady() || addingToCart || state._added) ? 'disabled aria-disabled="true"' : '';
    const ctaLabel = addingToCart ? 'Adding...' : (state._added ? 'Kit Added ✓' : 'Add Kit to Cart');
    const ctaTitle = missing.length && !addingToCart && !state._added ? `title="Pick: ${escapeHtml(missing.join(', '))}"` : '';

    // Capacity totals — shown once the current hardware path has a license
    // coverage tier. In hardware_includes_core mode that tier is the hidden
    // default bundle instead of a visible Core SKU selection.
    const capacityHtml = selectedSmaBundleId()
      ? `<div class="kc-summary__capacity">
          <div class="kc-summary__capacity-row">
            <span class="kc-summary__capacity-label">Total devices</span>
            <span class="kc-summary__capacity-value">${totalDevices().toLocaleString()}</span>
          </div>
          <div class="kc-summary__capacity-row">
            <span class="kc-summary__capacity-label">Total points</span>
            <span class="kc-summary__capacity-value">${totalPoints().toLocaleString()}</span>
          </div>
        </div>`
      : '';

    return `
      <div class="kc-summary">
        <div class="kc-summary__header">
          <h2 class="kc-summary__title">Your Kit</h2>
          <button type="button" class="kc-summary__reset" data-kc-action="reset" ${lines.length === 0 ? 'hidden' : ''}>Start Over</button>
        </div>
        <div class="kc-summary__lines">${linesHtml}</div>
        ${capacityHtml}
        <div class="kc-summary__subtotal">
          <span>Subtotal</span>
          <strong>${formatMoney(subtotalCents())}</strong>
        </div>
        <button type="button" class="kc-summary__cta" data-kc-action="add-to-cart" ${ctaDisabled} ${ctaTitle}>
          ${ctaLabel}
        </button>
        ${state._added ? `<p class="kc-summary__hint">Click <strong>Start Over</strong> to build another kit.</p>` : (missing.length ? `<p class="kc-summary__hint">Missing: ${escapeHtml(missing.join(', '))}</p>` : '')}
      </div>
      <div class="kc-summary__bar" ${lines.length === 0 ? 'hidden' : ''}>
        <div class="kc-summary__bar-total">
          <strong>${formatMoney(subtotalCents())}</strong>
          ${missing.length && !addingToCart && !state._added ? `<span class="kc-summary__bar-hint">Missing: ${escapeHtml(missing.join(', '))}</span>` : '<span class="kc-summary__bar-hint">Subtotal</span>'}
        </div>
        <button type="button" class="kc-summary__cta kc-summary__bar-cta" data-kc-action="add-to-cart" ${ctaDisabled} ${ctaTitle}>
          ${ctaLabel}
        </button>
      </div>`;
  }

  // ─────────────────────────────────────────────────────────────────
  //  Event binding (delegated once; survives full re-renders)
  // ─────────────────────────────────────────────────────────────────
  function bindEvents() {
    if (eventsBound) return;
    eventsBound = true;

    // Capture phase: runs before the handlers below call setState. Taken from
    // the event target, not document.activeElement — Safari doesn't focus
    // radios/checkboxes on click.
    ['click', 'change'].forEach(type => sectionsHost.addEventListener(type, event => {
      const block = event.target.closest && event.target.closest('[data-kc-block]');
      interactionBlock = block ? block.getAttribute('data-kc-block') : null;
    }, true));

    sectionsHost.addEventListener('change', event => {
      const input = event.target.closest('[data-kc-action]');
      if (!input || !sectionsHost.contains(input)) return;
      const action = input.getAttribute('data-kc-action');
      if (action === 'select-brand') return setState({ brand: input.value });
      if (action === 'select-hardware') return setState({ hardwareId: input.value });
      if (action === 'select-sd-card') return setState({ sdCard: input.value === 'yes' });
      if (action === 'select-core') return setState({ coreTier: input.value });
      if (action === 'toggle-sma') {
        const next = input.checked
          ? Array.from(new Set([...state.smaTerms, input.value]))
          : state.smaTerms.filter(t => t !== input.value);
        return setState({ smaTerms: next });
      }
      if (action === 'toggle-pack') {
        const next = input.checked
          ? Array.from(new Set([...state.devicePacks, input.value]))
          : state.devicePacks.filter(p => p !== input.value);
        return setState({ devicePacks: next });
      }
      if (action === 'toggle-addon') {
        const next = { ...state.addons };
        if (input.checked) {
          const hw = hardware();
          const ad = hw && Array.isArray(hw.addons) ? hw.addons.find(a => a.id === input.value) : null;
          next[input.value] = clampQty(ad && ad.defaultQty > 0 ? ad.defaultQty : 1);
        } else {
          delete next[input.value];
        }
        return setState({ addons: next });
      }
      if (action === 'addon-qty-input') return setState({ addons: { ...state.addons, [input.getAttribute('data-addon-id')]: clampQty(input.value) } });
      if (action === 'toggle-accessory') {
        const next = { ...state.accessories };
        if (input.checked) {
          const hw = hardware();
          const acc = hw && Array.isArray(hw.accessories) ? hw.accessories.find(a => a.id === input.value) : null;
          next[input.value] = clampQty(acc && acc.defaultQty > 0 ? acc.defaultQty : 1);
        } else {
          delete next[input.value];
        }
        return setState({ accessories: next });
      }
      if (action === 'acc-qty-input') return setState({ accessories: { ...state.accessories, [input.getAttribute('data-acc-id')]: clampQty(input.value) } });
      if (action === 'select-driver-needed') return setState({ driverNeeded: input.value === 'yes' });
      if (action === 'select-driver-brand') return setState({ driverBrand: input.value });
      if (action === 'select-driver-points') return setState({ driverPoints: input.value });
      if (action === 'select-driver-product') return setState({ driverProductHandle: input.value });
      if (action === 'toggle-driver-companion') {
        const next = input.checked
          ? Array.from(new Set([...state.driverCompanions, input.value]))
          : state.driverCompanions.filter(h => h !== input.value);
        return setState({ driverCompanions: next });
      }
      if (action === 'qty-input') return setState({ quantity: clampQty(input.value) });
    });

    sectionsHost.addEventListener('click', event => {
      // The SMA info icon sits inside its card's <label>; a tap there is for
      // reading the tooltip, not for toggling the card's checkbox.
      if (event.target.closest('.kc-tooltip')) {
        event.preventDefault();
        return;
      }
      const el = event.target.closest('[data-kc-action]');
      if (!el || !sectionsHost.contains(el)) return;
      const action = el.getAttribute('data-kc-action');
      const radioKey = {
        'select-brand': 'brand',
        'select-hardware': 'hardwareId',
        'select-core': 'coreTier',
        'select-driver-brand': 'driverBrand',
        'select-driver-points': 'driverPoints',
        'select-driver-product': 'driverProductHandle'
      }[action];
      if (radioKey && state[radioKey] === el.value) return setState({ [radioKey]: el.value });
      // Re-picking the current SD answer after Edit fires no 'change'; collapse the step.
      if (action === 'select-sd-card' && state.sdCard === (el.value === 'yes')) return setState({ sdCard: state.sdCard });
      if (action === 'addon-qty-dec' || action === 'addon-qty-inc') {
        const handle = el.getAttribute('data-addon-id');
        const cur = state.addons[handle] || 1;
        const next = { ...state.addons };
        if (action === 'addon-qty-dec' && cur <= 1) delete next[handle];
        else next[handle] = clampQty(cur + (action === 'addon-qty-inc' ? 1 : -1));
        return setState({ addons: next });
      }
      if (action === 'acc-qty-dec' || action === 'acc-qty-inc') {
        const handle = el.getAttribute('data-acc-id');
        const cur = state.accessories[handle] || 1;
        const next = { ...state.accessories };
        if (action === 'acc-qty-dec' && cur <= 1) delete next[handle];
        else next[handle] = clampQty(cur + (action === 'acc-qty-inc' ? 1 : -1));
        return setState({ accessories: next });
      }
      if (action === 'qty-dec') return setState({ quantity: clampQty(state.quantity - 1) });
      if (action === 'qty-inc') return setState({ quantity: clampQty(state.quantity + 1) });
      if (action === 'edit-section') return setState({ _expandedSection: el.getAttribute('data-section') });
      // A <button> (driver load-error block) — buttons fire 'click', never 'change'.
      if (action === 'clear-driver-selection') {
        return setState({
          driverNeeded: null,
          driverBrand: null,
          driverPoints: null,
          driverProductHandle: null,
          driverCompanions: [],
          _expandedSection: 'drivers'
        });
      }
    });

    summaryHost.addEventListener('click', event => {
      const btn = event.target.closest('[data-kc-action]');
      if (!btn || !summaryHost.contains(btn)) return;
      const action = btn.getAttribute('data-kc-action');
      if (action === 'reset') {
        const ok = window.confirm('Clear all selections and start a new kit?');
        if (!ok) return;
        emitKitEvent('reset_clicked', { kit_reset_confirmed: true });
        state = { ...DEFAULT_STATE, devicePacks: [], smaTerms: [], accessories: {}, addons: {}, driverCompanions: [] };
        clearStorage();
        render();
        return;
      }
      if (action !== 'add-to-cart') return;
      if (addingToCart || state._added || !isReady()) return;
      const bcs = getBCS();
      if (!bcs || typeof bcs.addItemsToCart !== 'function') {
        console.error('[kit-configurator] window.BCSProductLookup.addItemsToCart not available at click time');
        showToast("Couldn't add to cart — product lookup module unavailable.", 'error');
        return;
      }
      const submissionId = makeCartSubmissionId();
      const payload = buildCartPayload(buildCartLines(), submissionId);
      emitKitEvent('add_to_cart_clicked', {
        kit_submission_id: submissionId,
        kit_payload_line_count: payload.length
      });
      addingToCart = true;
      render();  // immediate feedback: CTA → "Adding...", all inputs disabled
      const supportsAbortController = typeof AbortController === 'function';
      const controller = supportsAbortController ? new AbortController() : null;
      let timeoutFired = false;
      let timeoutHandle = null;
      if (controller) {
        timeoutHandle = setTimeout(() => {
          timeoutFired = true;
          controller.abort();
        }, CART_ADD_TIMEOUT_MS);
      }
      const addPromise = supportsAbortController
        ? bcs.addItemsToCart(payload, { signal: controller.signal })
        : withTimeout(bcs.addItemsToCart(payload, {}), CART_ADD_TIMEOUT_MS, 'Cart add timed out');
      Promise.resolve(addPromise)
        .then(() => {
          if (timeoutHandle) clearTimeout(timeoutHandle);
          addingToCart = false;
          state._added = true;
          clearStorage();
          emitKitEvent('add_to_cart_succeeded', {
            kit_submission_id: submissionId,
            kit_payload_line_count: payload.length,
            kit_timed_out: false
          });
          showToast('Kit added to cart.', 'success', { label: 'View Cart ->', href: '/cart' });
          render();
        })
        .catch(async err => {
          if (timeoutHandle) clearTimeout(timeoutHandle);
          const timedOut = timeoutFired || (err && typeof err.message === 'string' && /timed out|aborted/i.test(err.message));
          if (timedOut) {
            try {
              for (let attempt = 0; attempt < 6; attempt += 1) {
                const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => (r.ok ? r.json() : null));
                if (cartMatchesSubmission(cart, payload, submissionId)) {
                  addingToCart = false;
                  state._added = true;
                  clearStorage();
                  emitKitEvent('add_to_cart_succeeded', {
                    kit_submission_id: submissionId,
                    kit_payload_line_count: payload.length,
                    kit_timed_out: true,
                    kit_timeout_recovered: true
                  });
                  showToast('Kit added to cart.', 'success', { label: 'View Cart ->', href: '/cart' });
                  render();
                  return;
                }
                if (attempt < 5) {
                  await delay(500);
                }
              }
            } catch (confirmErr) {
              console.warn('[kit-configurator] cart confirmation after timeout failed', confirmErr);
            }
            addingToCart = false;
            console.error('[kit-configurator] cart-add timed out before Shopify confirmed the order', err);
            emitKitEvent('add_to_cart_failed', {
              kit_submission_id: submissionId,
              kit_payload_line_count: payload.length,
              kit_timed_out: true,
              kit_timeout_recovered: false,
              kit_error: 'timeout'
            });
            showToast("Cart add timed out before Shopify confirmed it. Please check your cart before trying again.", 'error');
            render();
            return;
          }
          addingToCart = false;
          console.error('[kit-configurator] cart-add failed', err);
          emitKitEvent('add_to_cart_failed', {
            kit_submission_id: submissionId,
            kit_payload_line_count: payload.length,
            kit_timed_out: false,
            kit_error: err && err.message ? err.message : 'unknown'
          });
          showToast("Couldn't add to cart. Please try again.", 'error');
          render();
        });
    });

    let summaryBarFrame = 0;
    const queueSummaryBarUpdate = () => {
      // The theme editor replaces the section on reload; drop this instance's listeners.
      if (!root.isConnected) {
        window.removeEventListener('scroll', queueSummaryBarUpdate);
        window.removeEventListener('resize', queueSummaryBarUpdate);
        return;
      }
      if (summaryBarFrame) return;
      summaryBarFrame = requestAnimationFrame(() => { summaryBarFrame = 0; updateSummaryBar(); });
    };
    window.addEventListener('scroll', queueSummaryBarUpdate, { passive: true });
    window.addEventListener('resize', queueSummaryBarUpdate);
  }

  // Mobile summary bar (.kc-summary__bar) duplicates the CTA. Show it only
  // while the full summary's CTA is still below the screen: a tall summary
  // can be partly on screen with its CTA out of reach, and once the CTA has
  // scrolled above the screen the bar would only cover the footer. The class
  // on <html> lets the toast (appended to body) clear the bar.
  const mobileLayout = window.matchMedia('(max-width: 768px)');
  function updateSummaryBar() {
    if (!mobileLayout.matches) {
      summaryHost.classList.remove('kc-summary-col--bar-shown');
      document.documentElement.classList.remove('kc-summary-bar-shown');
      return;
    }
    const bar = summaryHost.querySelector('.kc-summary__bar');
    const cta = summaryHost.querySelector('.kc-summary > .kc-summary__cta');
    const shown = !!(bar && !bar.hidden && cta && cta.getBoundingClientRect().bottom > window.innerHeight);
    summaryHost.classList.toggle('kc-summary-col--bar-shown', shown);
    document.documentElement.classList.toggle('kc-summary-bar-shown', shown);
  }

  // ─────────────────────────────────────────────────────────────────
  //  Dev console hook
  // ─────────────────────────────────────────────────────────────────
  // Read-only debug hook. `setState` deliberately omitted so DevTools can't push
  // invalid state. Restricted brand SKU data is not emitted into SKU_MAP.
  window.__KC = { BRAND_REGISTRY, SKU_MAP, get state() { return state; }, render, root };

  render();
  trackKitPageViewOnce();

  // Load the full driver collection through the B2B-aware Liquid JSON view.
  if (KC_CONFIG.driverCollectionHandle) {
    fetchDriversViaCollectionView()
      .then(drivers => {
        driversLoading = false;
        if (drivers && drivers.length) {
          applyDrivers(drivers, { pricesTrusted: true });
          validateRestoredDriverState();
        }
        emitKitEvent('driver_catalog_loaded', {
          kit_driver_mode: 'collection_view',
          kit_driver_collection_handle: KC_CONFIG.driverCollectionHandle || 'kit-drivers',
          kit_driver_count: Array.isArray(drivers) ? drivers.length : 0
        });
        render();
      })
      .catch(err => {
        driversLoading = false;
        driversLoadError = true;
        DRIVERS = [];
        emitKitEvent('driver_catalog_failed', {
          kit_driver_mode: 'collection_view',
          kit_driver_collection_handle: KC_CONFIG.driverCollectionHandle || 'kit-drivers',
          kit_error: err && err.message ? err.message : 'unknown'
        });
        console.error('[kit-configurator] Driver collection fetch failed; driver options unavailable', err);
        render();
        showToast('Driver options could not be loaded.', 'error');
      });
  }
  }  // end initKitConfigurator

  // ─────────────────────────────────────────────────────────────────
  //  Dispatcher — runs initKitConfigurator on every matching root,
  //  on page load and on Theme Customizer section reloads.
  // ─────────────────────────────────────────────────────────────────
  function initAll() {
    document.querySelectorAll('[data-kit-configurator]').forEach(initKitConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-kit-configurator]');
    if (root) initKitConfigurator(root);
  });
})();
